/**
 * English interface table: the source every other table is typed against
 * (`AppTranslations = typeof table`), and the fallback for any locale or key
 * that has none (ADR-111). It is the one table bundled with the app.
 *
 * Pattern: typed nested objects. Functions handle interpolation and
 * pluralization.
 *
 * Sections
 *  landing     — marketing landing page (folds in the old i18n/landing.ts)
 *  onboarding  — first-run wizard
 *  home        — Home page (chameleon CTA, greetings, error states)
 *  learn       — Learn session page chrome
 *  complete    — LearningComplete screen
 */
import { formLabelFrom } from '../../formLabels'
import { FORM_LABEL_EN } from './formLabels'


export const table = {
  // ── Landing (moved from i18n/landing.ts) ──────────────────────────────────
  nav: {
    signIn: 'Sign in',
    getStarted: 'Get started free',
    // ── App navigation ──────────────────────────────────────────────────────
    home: 'Home',
    library: 'Library',
    learn: 'Learn',
    progress: 'Progress',
    grammar: 'Grammar',
    profile: 'Profile',
    settings: 'Settings',
    signOut: 'Sign out',
    lightMode: 'Light mode',
    darkMode: 'Dark mode',
    upgradePro: 'Upgrade to Pro',
    expandSidebar: 'Expand sidebar',
    collapseSidebar: 'Collapse sidebar',
    // Sidebar quick-lookup entry — opens the Ctrl/Cmd+K overlay; not a route,
    // so it is a plain button styled like a nav item rather than a NavLink.
    quickLookup: 'Quick lookup',
  },

  // ── Shared layout chrome ─────────────────────────────────────────────────
  layout: {
    // ExplanationLangBanner — "Explanations are in [DE]" + revert button
    explBanner: 'Explanations are in',
    revertExpl: 'Revert to',
    trialToast: (days: number) =>
      `Pro trial active — ${days} day${days !== 1 ? 's' : ''} remaining. See Settings › Account.`,
    // ThemeToggle — title + aria-label, same string for both
    switchToLight: 'Switch to light mode',
    switchToDark:  'Switch to dark mode',
    // ThemeSwitch (sidebar foot) — group aria-label for the two-state control
    themeSwitchLabel: 'Theme',
  },

  // ── GoogleSignInButton (LoginPage / RegisterPage) ────────────────────────
  auth: {
    continueWithGoogle:  'Continue with Google',
    redirecting:         'Redirecting…',
    googleUnavailable:   'Google sign-in is not available right now.',
    googleGenericError:  'Could not start Google sign-in. Please try again.',
  },
  loginPage: {
    subtitle: 'Welcome back — sign in to continue',
    or: 'or',
    email: 'Email',
    password: 'Password',
    submit: 'Sign in',
    submitting: 'Signing in…',
    noAccount: 'No account?',
    createOne: 'Create one',
    invalid: 'Invalid email or password',
  },
  registerPage: {
    title: 'Create account',
    subtitle: 'Start learning with spaced repetition',
    displayName: 'Display name',
    namePlaceholder: 'Your name (optional)',
    email: 'Email',
    password: 'Password',
    passwordPlaceholder: 'Min. 8 characters',
    submit: 'Create account',
    submitting: 'Creating account…',
    haveAccount: 'Already have an account?',
    signIn: 'Sign in',
    signUpWithGoogle: 'Sign up with Google',
    or: 'or',
    passwordTooShort: 'Password must be at least 8 characters',
    emailTaken: 'An account with this email already exists.',
    signInInstead: 'Sign in instead?',
    failed: 'Registration failed. Please try again.',
  },

  // ── Add content sheet ────────────────────────────────────────────────────
  addContent: {
    title: 'Add',
    close: 'Close',
    eyebrowWords: 'Add words',
    eyebrowCreate: 'Create',
    eyebrowFind: 'Find ready-made',
    lookup:   { label: 'Look up a word',     desc: 'Translation, examples — straight into a deck' },
    fromText: { label: 'From text or photo', desc: 'Mark words — get cards' },
    fromTable:{ label: 'From a spreadsheet', desc: 'CSV or Excel: columns become fields' },
    newDeck:  { label: 'New deck',           desc: 'Empty — you add the cards' },
    newText:  { label: 'New text',           desc: 'A gap exercise or an essay' },
    catalog:  { label: 'Deck catalog',       desc: 'Sets by level and topic' },
  },
  textFlow: {
    entryWords: 'Words from a text',
    entryEssay: 'New text',
    stepOf: (n: number) => `${n} of 3`,
    stepSubtitle: (n: number, name: string) => `Step ${n} of 3 · ${name}`,
    stepNames: { source: 'source', words: 'words', save: 'saving' },
    close: 'Close',
    back: 'Back',
    stepShort: (n: number) => `Step ${n}`,
    leave: {
      title: 'Leave without saving?',
      body: 'The text you entered and your marks will be lost.',
      leave: 'Leave',
      stay: 'Stay',
    },
    source: {
      placeholder: 'Paste an article, a book page or your own essay…',
      placeholderDesktop: 'Or drop a file or an image here.',
      paste: 'Paste',
      photo: 'Photo',
      image: 'Image',
      file: 'File',
      helper: 'A photo is read on your phone and never sent anywhere. A file is .txt, .docx, .pdf or .pptx with text.',
      helperDesktopEmpty: 'Ctrl+V works too. A file is .txt, .docx, .pdf or .pptx with text.',
      wordCount: (n: number) => `${n} ${n === 1 ? 'word' : 'words'}`,
      next: 'Next',
      photoBanner: 'Text read from the photo. Check it for typos — you can fix them right here.',
      imageBanner: 'Text read from the image. Check it for typos — you can fix them right here.',
      fileBanner: (name: string) => `Text from the file “${name}”. You can edit it right here.`,
      replacePhoto: 'Replace photo',
      replaceImage: 'Replace image',
      replaceFile: 'Replace file',
      dropHere: 'Drop the file or image here',
      reading: 'Opening the file…',
      analysing: 'Analysing the text…',
      tooLong: (n: number) => `The text is longer than ${n} characters — shorten it`,
      ocrNothing: "Couldn't recognise any text. Try shooting closer or in better light.",
      fileUnreadable: "Couldn't open the file. A .txt, .docx or a PDF with text works.",
      pasteFailed: "Couldn't paste. Paste into the field by hand (press and hold).",
      analyseFailed: "Couldn't analyse the text. Check your connection and try again.",
    },
    words: {
      summary: (n: number) => `${n} distinct ${n === 1 ? 'word' : 'words'} · each in one group`,
      groups: {
        marked:   { name: 'Marked',        desc: 'Will become cards or gaps. Tap a word to remove it; the role is changed in the text.' },
        studying: { name: 'Already studying', desc: 'Words you already have a card for. Tap one to add another card.' },
        other:    { name: 'Other words',   desc: 'At your level or below, not learned yet.' },
        known:    { name: 'Already known', desc: 'Words you have already learned or marked as known.' },
        function: { name: 'Function words', desc: 'Articles, prepositions, conjunctions, pronouns, numbers.' },
        names:    { name: 'Names',         desc: 'People’s names and place names.' },
      },
      moved: (word: string) => `“${word}” moved to “Marked”`,
      undo: 'Undo',
      cards: (n: number) => `${n} ${n === 1 ? 'card' : 'cards'}`,
      noGaps: 'no gaps',
      next: 'Next',
      empty: 'No words found in this text.',
      groupToggle: (name: string, n: number) => `${name}, ${n}`,
      gaps: (n: number) => `${n} ${n === 1 ? 'gap' : 'gaps'}`,
      viewText: 'In the text',
      viewList: (n: number) => `As a list ${n}`,
      viewSwitch: 'View',
      meta: (words: number, level: string | null) =>
        `${words} ${words === 1 ? 'word' : 'words'}` + (level ? ` · your level ${level}` : ''),
      suggestedOn: (n: number) => `${n} ${n === 1 ? 'word' : 'words'} above your level marked`,
      suggestedOff: 'Words above your level are not marked',
      dropSuggested: 'Clear',
      addSuggested: 'Suggest words',
      textLabel: 'Text. Tap a word to mark it.',
      panel: {
        selected: 'Selected',
        selectedPhrase: 'Selected · phrase',
        makePhrase: 'Make a phrase',
        role: 'Role',
        roles: { card: 'Card', gap: 'Gap', both: 'Both' },
        remove: 'Remove',
        removed: (w: string) => `“${w}” removed`,
        restore: 'Restore',
        idle: 'Tap a word to add or remove it. Phrase: hold and drag, or “Make a phrase”.',
        idleDesktop: 'Click a word in the text to mark it.',
        hintDesktop: 'Click to mark or unmark. Drag across words for a phrase. Shift+click: a phrase from the selected word to this one.',
        phrase: 'Phrase',
        phraseCount: (n: number) => `Phrase · ${n}`,
        waiting: 'Tap the last word of the phrase',
        cancel: 'Cancel',
        noTranslation: 'translation will come from the dictionary',
        inCard: (term: string, translation: string) => `On the card: ${term} — ${translation}`,
        gapPlaces: (k: number) => `Gap: ${k} ${k === 1 ? 'place' : 'places'} in the text`,
        gapOnly: 'Not a dictionary word — it can only be a gap.',
      },
      matchFailed: 'Could not show where else the gaps occur. Your marks are kept; the text is checked when you save.',
      retry: 'Retry',
      lost: (forms: string) => `Will not become gaps: ${forms} — longer gaps took their place.`,
      roleSuffix: { gap: 'gap', both: 'both' },
      // “Role for all” (ADR-115 Decision 3: the gap limit is the server's).
      bulk: {
        label: 'Role for all',
        tooMany: (n: number) => `No more than ${n} gaps — unmark some words`,
        applied: (role: string) => `Role “${role}” for all marks.`,
      },
    },
    save: {
      title: 'Saving',
      titleLabel: 'Text title',
      nothingTitle: 'Nothing marked',
      nothingBody: 'The text will be saved as plain text — no cards and no gaps. You can mark words later by opening the text.',
      cardsLabel: (n: number): string => (n === 1 ? 'card' : 'cards'),
      gapsLabel: (n: number): string => (n === 1 ? 'gap' : 'gaps'),
      ofTotal: (n: number) => `of ${n}`,
      cardsTodayLabel: 'cards today',
      whereLabel: 'Where to save',
      newDeck: { title: 'New deck', desc: 'Gets the text’s title. Rename it later.' },
      existingDeck: { title: 'Existing deck', choose: 'Choose a deck' },
      noDeck: {
        title: 'No deck',
        chosen: 'Chosen because there are no cards. The text will be in Library → Mine → Texts.',
        blocked: 'Unavailable: cards need a deck. Remove all cards to save only the text.',
      },
      saveTextSwitch: { title: 'Save the text', locked: 'Required: gaps live in the text', free: 'Off: only the cards are added, the text is not kept' },
      queueSwitch: { title: 'Learn in the daily queue', desc: 'The cards will appear in “Learn” on Home' },
      pickerTitle: 'Choose a deck',
      pickerSearch: 'Search your decks',
      pickerEmpty: 'No decks found.',
      pickerLoading: 'Loading…',
      skipped: (n: number) => `${n} ${n === 1 ? 'word is' : 'words are'} already in this deck and won’t be added again.`,
      buttonTextAndCards: (n: number) => `Save the text and ${n} ${n === 1 ? 'card' : 'cards'}`,
      buttonCardsOnly: (n: number) => `Add ${n} ${n === 1 ? 'card' : 'cards'}`,
      buttonTextOnly: 'Save the text',
      saving: 'Saving…',
      saveFailed: "Couldn't save. Check your connection and try again. Nothing was lost.",
      tooLong: (n: number) => `The text is longer than ${n} characters — shorten it`,
      tooManyGaps: (n: number) => `A text can have at most ${n} gaps.`,
      budget: {
        panelTitle: (left: number) => `From texts you can add ${left} more ${left === 1 ? 'word' : 'words'} today`,
        panelBody: (k: number, when: string) => `The text and its gaps will be saved in full. These ${k} ${k === 1 ? 'word' : 'words'} will become cards when the limit refreshes — ${when}.`,
        exhaustedTitle: 'Words from texts are used up for today',
        exhaustedBody: (k: number, when: string) => `The text and its gaps will be saved in full. All ${k} ${k === 1 ? 'word' : 'words'} will become cards when the limit refreshes — ${when}.`,
        cardsOnlyTitle: (k: number) => `${k} ${k === 1 ? 'word' : 'words'} won’t become cards today`,
        cardsOnlyBody: (left: number, when: string) => `Today you can add ${left} more ${left === 1 ? 'word' : 'words'} from texts. The rest are not created if you save without the text. Turn on “Save the text” and they will wait and be added when the limit refreshes — ${when}.`,
        cardsOnlyAction: 'Save the text too',
        today: (time: string) => `today at ${time}`,
        tomorrow: (time: string) => `tomorrow at ${time}`,
        later: (date: string, time: string) => `${date} at ${time}`,
      },
    },
    saved: {
      toastSaved: (cards: number, gaps: number, waiting: number, when: string) =>
        `Text saved: ${cards} ${cards === 1 ? 'card' : 'cards'} and ${gaps} ${gaps === 1 ? 'gap' : 'gaps'}.` +
        (waiting > 0 ? ` ${waiting} ${waiting === 1 ? 'word' : 'words'} will become cards ${when}.` : ''),
      toastTextOnly: (gaps: number) => `Text saved with ${gaps} ${gaps === 1 ? 'gap' : 'gaps'}.`,
      toastCards: (cards: number) => `${cards} ${cards === 1 ? 'card' : 'cards'} added.`,
      toastBudgetHit: 'Saved. Some words did not become cards — today’s limit for words from texts.',
      notCreated: (words: string) => `Not created today: ${words}.`,
      generating: (done: number, total: number) => `Making cards… ${done} of ${total}`,
      generatingStart: 'Making cards…',
      generated: 'The cards are ready.',
      generatedSome: (done: number, total: number) => `${done} of ${total} cards were made.`,
      genFailed: 'The cards could not be created.',
      genPartial: 'Some cards could not be created.',
      genPending: 'Some cards are waiting for content. You can fill them in the deck.',
      genDetails: 'Details',
      statusUnreachable: "Couldn't check the cards’ progress. Open the deck to see them.",
    },
  },
  hero: {
    badge: 'Spaced repetition · FSRS algorithm',
    headline: 'Learn any language,\none word at a time',
    sub: 'Turn any text into personalized vocabulary cards. Smart scheduling surfaces the right word at the right moment — proven to outperform classic flashcard systems.',
    ctaPrimary: 'Start learning free',
    ctaSecondary: 'Sign in',
    stat1: { value: '8 levels', label: 'Mastery depth' },
    stat2: { value: 'FSRS', label: 'Algorithm' },
    stat3: { value: '6+', label: 'Language pairs' },
  },
  howItWorks: {
    title: 'How it works',
    steps: [
      {
        n: '01',
        title: 'Import any text',
        body: 'Paste an article, book excerpt, or song lyrics in your target language. CardSmith extracts words, detects their CEFR level, and builds a ready-to-study deck automatically.',
        icon: 'file-text',
      },
      {
        n: '02',
        title: 'Study with FSRS',
        body: 'The FSRS algorithm schedules each word at the optimal interval. Early reps are close together; mastered words surface weeks later. Zero wasted reviews.',
        icon: 'brain',
      },
      {
        n: '03',
        title: 'Watch mastery grow',
        body: 'Words progress through 8 levels — from New to Mastered. Mastered words retire from your queue. Your vocabulary grows, counted and visible.',
        icon: 'trending-up',
      },
    ],
  },
  features: {
    title: 'Everything you need to learn',
    subtitle: 'Built for serious language learners',
    items: [
      { icon: 'file-text', title: 'Text import', body: 'Paste any text → automatic word extraction, CEFR tagging, and deck creation in seconds.' },
      { icon: 'zap', title: 'Smart queue', body: 'All due cards from all your decks merged into one focused session. No switching required.' },
      { icon: 'globe', title: 'Language pairs', body: 'Definitions, examples, and grammar explained in your native language. Supports RU→EN, EN→EN, FR→EN and more.' },
      { icon: 'layers', title: 'Two study modes', body: 'Classic flip cards for quick review. Typing mode forces active recall — the most effective encoding method.' },
      { icon: 'sparkles', title: 'AI tutor', body: 'Ask for etymology, usage examples, or common mistakes. Context in your native language.' },
      { icon: 'wifi-off', title: 'Offline-ready', body: 'Install as a PWA. Study on the subway or a plane without an internet connection.' },
    ],
  },
  pro: {
    badge: 'PRO',
    title: 'Go deeper with Pro',
    price: '$8',
    per: '/ month',
    desc: 'Curated CEFR courses, grammar in your native language, and unlimited AI-powered card generation.',
    features: [
      'CEFR-graded course library (A1–C2)',
      'Grammar articles in your native language',
      'Advanced AI explanations and sentence check',
      'Unlimited AI card generation',
      'Priority support',
    ],
    cta: 'Upgrade to Pro',
    note: 'Free plan always available. No credit card required to start.',
  },
  demo: {
    title: 'Try it — paste any English text',
    placeholder: 'The curious scientist carefully studied each luminescent specimen under the microscope.',
    submit: 'Extract vocabulary',
    loading: 'Analysing…',
    charLimit: 'Max 200 characters',
    ctaResult: 'Register to study these words →',
    emptyCaption: 'Example results',
    wordsFound: 'words found',
    noLanguage: 'Language not detected',
    errors: {
      tooLong: 'Text exceeds 200 characters.',
      rateLimit: 'Too many requests. Try again in a few minutes.',
      unsupportedLanguage: "We don't support this language yet.",
      unknown: 'Something went wrong. Please try again.',
    },
    // The empty-state example words' translations into this language, by lemma.
    // English has none: an English-speaking visitor sees the words alone.
    previewTranslations: {} as Record<string, string>,
  },
  footer: {
    tagline: 'Built for daily vocabulary practice.',
    links: { signIn: 'Sign in', register: 'Register', explore: 'Explore', pro: 'Pro plan' },
    copy: '© 2026 CardSmith',
  },

  // ── Onboarding ────────────────────────────────────────────────────────────
  onboarding: {
    welcomeHeadline: 'Learn 5000 words. Speak English.',
    welcomeSub: 'Vocabulary that sticks — powered by spaced repetition and real language context.',
    getStarted: 'Get started →',
    settingUp: 'Setting up…',
    sourceLangTitle: 'What language do you speak?',
    sourceLangSub: "We'll show explanations and translations in this language.",
    targetLangTitle: 'What language do you want to learn?',
    targetLangSub: 'More languages coming soon.',
    levelTitle: (lang: string) => `How well do you know ${lang}?`,
    levelSub: 'This helps us start you at the right vocabulary level.',
    continue: 'Continue',
    startLearning: 'Start learning',
    levels: {
      A1: { label: 'Beginner',           desc: 'I know almost nothing' },
      A2: { label: 'Elementary',         desc: 'I can use simple phrases' },
      B1: { label: 'Intermediate',       desc: 'I can hold basic conversations' },
      B2: { label: 'Upper Intermediate', desc: 'I can discuss most topics' },
    },
    error: 'Something went wrong. Please try again.',
    comingSoon: 'Coming soon',
    orSeparator: 'or',
    levelNotSure: 'Not sure about your level? Take a quick test',
    placementTitle: 'Do you know these words?',
    placementSub: 'Tap each word you recognise — helps us set the right starting level.',
    placementSubmit: 'Estimate my level',
    placementBack: '← Pick manually',
    placementLoading: 'Loading test…',
    placementResultBanner: (level: string) => `Estimated level: ${level}`,
    placementEmpty: 'Test unavailable — please pick your level manually.',
    // Adaptive v2 strings (ADR-051)
    placementDisclosureTitle: 'Quick level check',
    placementDisclosureBody: 'You\'ll see ~40 words one at a time. Some aren\'t real words — that\'s intentional. Tap "I know this" only if you genuinely know the word.',
    placementDisclosureStart: 'Start test',
    placementItemProgress: (current: number, total: number) => `${current} / ${total}`,
    placementYes: 'I know this',
    placementNo: 'Don\'t know',
    placementResultTitle: 'Your level',
    placementResultSub: (level: string) => `We estimate your level at ${level}`,
    placementResultLowConfidence: 'Result may be less accurate — consider picking your level manually.',
    placementResultConfirm: 'Use this level',
    placementResultManual: 'Pick manually',
    unsupportedPair: 'This language combination isn\'t available yet. Go back and choose a different target language.',
    // ADR-111 D5: the server refused to newly set a language that is not released yet.
    languageNotReleased: 'That language isn’t available yet. Go back and choose another.',
  },

  // ── Home page ─────────────────────────────────────────────────────────────
  home: {
    greeting: {
      morning:   'Good morning',
      afternoon: 'Good afternoon',
      evening:   'Good evening',
    },
    today:              (goal: number) => `/${goal} today`,
    streak:             (n: number) => `${n}d streak`,
    // Unit-only text for the split number/unit ignite entrance (DailyGoalFlame) — must stay
    // in sync with `streak` above, which remains the single-string a11y fallback.
    streakUnit:         (_n: number) => `d streak`,
    study:              (total: number, mins: number) => `Study · ${total} cards · ~${mins} min`,
    starting:           'Starting…',
    continue:           'Continue',
    continueProgress:   (done: number, total: number) => `Continue · ${done}/${total}`,
    manageQueue:        'Manage queue',
    manageStudySources: 'Manage study sources',
    newCards:           (n: number) => `${n} new`,
    reviewCards:        (n: number) => `${n} review`,
    newAndReview:       (n: number, r: number) => `${n} new · ${r} review`,
    emptyStream:        'Your study stream is empty. Add a deck to start learning.',
    browseDecks:        'Browse decks',
    allCaughtUp:        "You're all caught up",
    nothingDue:         'Nothing due right now. Check back later.',
    loadError:          "Couldn't load your learning plan",
    loadErrorSub:       'Check your connection and try again.',
    retry:              'Retry',
    back:               'Back',
    sessionError:       "Couldn't start session",
    sessionErrorSub:    'Something went wrong. Try again or go back.',
    jumpBackIn:         'Jump back in',
    weakSpots:          'Weak spots',
    topicNothingToPractise: (topic: string) => `Nothing to practise in ${topic} yet. Topic training uses words you have already answered — study some words from this topic first.`,
    addContent:         'Add content',
    deckCardsDue:       (total: number, due: number) => `${total} cards · ${due} due today`,
    deckCards:          (total: number) => `${total} cards`,
  },

  // ── Learn page ────────────────────────────────────────────────────────────
  learn: {
    startingSession:  'Starting session…',
    loadingCard:      'Loading card…',
    somethingWrong:   'Something went wrong',
    sessionError:     'Could not start your learning session. Check your connection and try again.',
    back:             '← Back',
    tryAgain:         'Try again',
    allCaughtUp:      'All caught up!',
    cardsReadyNow:    'Cards are ready now — try refreshing.',
    nextCardMins:     (n: number) => `Next card due in ${n} minute${n !== 1 ? 's' : ''}.`,
    nextCardHours:    (n: number) => `Next card due in ${n} hour${n !== 1 ? 's' : ''}.`,
    nextCardDate:     (date: string) => `Next review: ${date}.`,
    checkBackLater:   'Check back later.',
    practiceEmptyTitle: 'Nothing to practise yet',
    prepEmptyBody: 'Quick prep practises words from this deck that you have already answered and not yet mastered. Learn a few words from this deck first, then come back.',
    backToHome:       '← Back to home',
    backToDeck:       '← Back to deck',
    backToCourse:     '← Back to course',
    resumed:          '↩ Resumed',
    previous:         '← Previous',
    fillInBlank:      'Fill in the blank',
    fromMemory:       'From memory',
    more:             'More',
    less:             'Less',
    correctFeedback:  '✓ Correct!',
    correctAnswer:    'Correct answer',
    incorrect:        'Incorrect',
    typeToContinue:   'Type it to continue',
    // Soft-reject guidance renders inside the input placeholder (ADR-048 §7
    // amendment, 2026-07-15); the first-letter hint moved into the card's
    // blank instead. Tiers are longest-first — the caller measures actual
    // fit and picks the longest one that renders without clipping; the last
    // entry is the floor and is never dropped below.
    softRejectTiers:  ['Similar word — try again', 'Similar word'],
    masteryChange:    (delta: number, isMastered: boolean) =>
      `${delta > 0 ? `+${delta}` : delta} level${Math.abs(delta) !== 1 ? 's' : ''}${isMastered ? ' · Mastered!' : ''}`,
    correctMC:        (answer: string) => `Correct answer: ${answer}`,
    allSides:          'All sides',
    chooseCorrectTerm: 'Choose the correct term',
    correctOption:     '✓ Correct',
    press14:           'Press 1–4 to select',
    explainFor:             (word: string) => `Explanation for ${word}`,
    aiExplanation:          'AI Explanation',
    closeExplanation:       'Close explanation',
    generatingExplanation:  'Generating explanation…',
    couldNotLoadExplanation: "Couldn't load explanation",
    explanationError:       'Something went wrong. Check your connection and try again.',
    arrangeSentence:        'Arrange the sentence',
    hint:                   'Hint:',
    correctExcl:            '✓ Correct!',
    incorrectExcl:          '✗ Incorrect',
    correctLabel:           'Correct:',
    tapWordsToBuild:        'Tap words below to build the sentence',
    checking:               'Checking…',
    submitArrow:            'Submit →',
    confirmAnswerAria:      'Confirm answer',
    submitAnswerAria:       'Submit answer',
    speakingLabel:          'Speaking…',
    replayAudio:            'Replay audio',
    listenAndType:          'Listen and type',
    termLabel:              'Term',
    definitionLabel:        'Definition',
    exampleLabel:           'Example',
    typeCorrectAnswer:      'Type the correct answer',
    yourAnswer:             'Your answer',
    typeWhatYouHeard:       'Type what you heard…',
    // Custom-rendered placeholder overlay for the fill-in-blank / definition-only
    // answer input (LearnPage) — not a native `placeholder` attribute (Chromium
    // Android bullet-glyph workaround, see the input's own comments), but reads
    // identically to the person typing.
    typeMissingWord:        'Type the missing word…',
    typeAnswer:             'Type the answer…',
    checkBtn:               'Check',
    confirmBtn:             'Confirm',
    answerLabel:            'Answer',
    enterToConfirm:         (retype: boolean): string => `Enter to ${retype ? 'confirm' : 'submit'}`,
    incorrectTypeAbove:     'Incorrect — type the correct answer above',
    continueBtn:            'Continue',
    explainThisWord:        'Explain this word',
    blankPlaceholder:       'blank',
    pronounceWord:          'Pronounce word',
    closeDefinition:        'Close definition',
    backToCurrent:          '← Back to current',
    previousCardBadge:      'Previous card',
    backToCurrentCard:      '← Back to current card',
    retypeCoachTitle:       'Type the word to continue',
    retypeCoachBody:        'Wrong answers replay until typed correctly — it locks in the memory.',
    retypeCoachDismiss:     'Got it',
    // Grammatical-form descriptor shown under the Learn card term (CardSide.form,
    // backend/app/services/word_form.py). Stored English content, not a catalogue
    // string — English identity here, each locale maps it from its own
    // formLabels.ts (ADR-111 D2). Falls back to the
    // raw stored value for anything unrecognised (e.g. a future free-text
    // `form_label_override`), so an unmapped value degrades to English rather
    // than a blank or placeholder.
    formLabel: formLabelFrom(FORM_LABEL_EN),
    // ADR-100 Decision 7: the Learn control that shows the card's texts in the
    // reader's own language (its visible face is the language code itself).
    // One fixed name: the control is a pressed/unpressed toggle (aria-pressed),
    // so its name must not flip with its state.
    translateCard: 'Show this card in your language',
    // ADR-082 boundary overlay (backend/docs/DECISIONS.md) — a session belongs to
    // the day it began in. Two situations, one overlay, distinguished only by
    // body copy; the button (continueBtn, above) is shared. Neither is reworded
    // from the accepted RU source; EN is written to match register and length.
    dayBoundaryInterrupted: 'Midnight passed — this session has ended and your progress is saved. Start a new one to continue.',
    dayBoundaryReturned:    'Your session ended along with yesterday. Progress is saved, and the remaining cards are back in the queue.',
  },

  // ── Grammar note card ─────────────────────────────────────────────────────
  grammarNote: {
    quickNote:    'Quick grammar note',
    quickCheck:   'Quick check',
    showAnswer:   'Show answer',
    continueArrow: 'Continue →',
    dismiss:      'Dismiss',
  },

  // ── AI rate-limit state ───────────────────────────────────────────────────
  aiRateLimit: {
    dailyLimitReached:    'Daily limit reached',
    usageLine:            (used: number, limit: number) => `${used} of ${limit} AI explanations used today.`,
    upgradeLabel:         'Upgrade to Pro',
    proQuotaUsed:         "You've used your Pro daily AI quota. The counter resets at midnight UTC.",
    resetsInLess:         'Resets in less than a minute',
    resetsInMins:         (m: number) => `Resets in ${m}m`,
    resetsInHours:        (h: number) => `Resets in ${h}h`,
    resetsInHoursAndMins: (h: number, m: number) => `Resets in ${h}h ${m}m`,
  },

  // ── Session complete screen ───────────────────────────────────────────────
  complete: {
    prepared:      "You're prepared!",
    goalReached:   'Daily goal reached!',
    perfect:       'Perfect session!',
    sessionDone:   'Session complete!',
    practiceNote: 'Practice session — word levels did not change.',
    challengingWords: (correct: number, total: number) =>
      `${correct} out of ${total} challenging word${total !== 1 ? 's' : ''} from this text are now in your memory`,
    reviewed:      (n: number) => `You reviewed ${n} card${n !== 1 ? 's' : ''} this session`,
    correct:       'Correct',
    wrong:         'Wrong',
    accuracy:      'Accuracy',
    bySection:     'By section',
    todaysGoal:    "Today's goal",
    done:          '✓ Done!',
    moreToGoal:    (n: number) => `${n} more to reach your daily goal`,
    newWordsToday: 'New words today:',
    daysStreak:    (n: number) => `${n} day${n !== 1 ? 's' : ''} streak`,
    continueLearning: 'Continue learning',
    goHome:        'Go home',
    reviewMistakes:(n: number) => `Review ${n} mistake${n !== 1 ? 's' : ''} →`,
    blockCard:     (pct: number, total: number) => `${pct}% · ${total} card${total !== 1 ? 's' : ''}`,
    mistakesTitle: 'Session mistakes',
    mistakesClose: 'Close',
    mistakesYourAnswer: 'Your answer',
    share: 'Share',
    shareTitle: 'My CardSmith progress',
    shareCaption: (words: number, url: string) =>
      `I learned ${words} new word${words !== 1 ? 's' : ''} today on CardSmith! ${url}`,
    shareSavedFallback: 'Image saved — caption copied to clipboard',
    shareFailed: "Couldn't create the share image",
    milestone: {
      vocabularyEyebrow: 'Vocabulary milestone',
      streakEyebrow: 'Streak milestone',
      wordsLabel: (n: number) => `${n.toLocaleString()}-word milestone reached`,
      streakLabel: 'day streak',
      supportVocabulary: (threshold: number, total: number) =>
        `You've crossed the ${threshold.toLocaleString()}-word mark — ${total.toLocaleString()} words learned and counting.`,
      supportStreak: (n: number) =>
        `${n} days without missing one. This isn't motivation anymore — it's a habit.`,
      shareLabel: 'Share this milestone',
      shareCaptionVocabulary: (n: number, url: string) =>
        `I just crossed the ${n.toLocaleString()}-word milestone on CardSmith! ${url}`,
      shareCaptionStreak: (n: number, url: string) =>
        `I just hit a ${n}-day streak on CardSmith! ${url}`,
      nextVocabulary: (remaining: number) =>
        `${remaining.toLocaleString()} word${remaining !== 1 ? 's' : ''} to your next milestone`,
      nextStreak: (remaining: number) =>
        `${remaining} day${remaining !== 1 ? 's' : ''} to your next milestone`,
      // Share card content (Direction C — docs/design/milestone-share-card.html)
      category: 'English vocabulary trainer',
      totalSuffixVocabulary: (total: number) => { void total; return 'words learned so far' },
      totalSuffixStreak: 'days without a miss',
      previewTitle: 'Preview',
      previewClose: 'Close',
      fieldLight: 'Light',
      fieldInverted: 'Inverted',
    },
  },

  // ── Settings page ─────────────────────────────────────────────────────────
  settings: {
    title: 'Settings',
    account: {
      title: 'Account',
      email: 'Email',
      readOnly: 'read-only',
      displayName: 'Display name',
      plan: 'Plan',
      planTrial:      (days: number) => `Pro trial — ${days} day${days !== 1 ? 's' : ''} left`,
      planProLifetime: 'Pro — lifetime',
      planProRenews:  (date: string) => `Pro — renews ${date}`,
      planFree:        'Free plan',
      trialDaysLeft:  (n: number) => `${n} day${n !== 1 ? 's' : ''} remaining in your Pro trial`,
      upgrade:         'Upgrade →',
    },
    password: {
      title:           'Password',
      desc:            'Change the password you use to sign in. Other devices will be signed out.',
      current:         'Current password',
      newPassword:     'New password',
      confirm:         'Confirm new password',
      submit:          'Change password',
      saving:          'Saving…',
      changed:         'Password changed',
      errWrongCurrent: 'Current password is incorrect.',
      errTooShort:     'New password must be at least 8 characters.',
      errMismatch:     'New passwords do not match.',
      errGeneric:      'Could not change password. Please try again.',
      oauthOnly:       'You sign in with a connected account, so there is no password to change yet.',
    },
    notifications: {
      title:              'Notifications',
      dailyReminders:     'Daily study reminders',
      ariaToggle:         'Toggle daily study reminders',

      // ── One status word + one explanation per state. Each state is distinct
      // on screen; none of them is a plain greyed switch under an encouraging
      // hint, which is what made "blocked", "unsupported" and "busy" look
      // identical to a control that did nothing.
      statusChecking:     'Checking',
      hintChecking:       "Working out whether reminders are on for this device.",
      statusOn:           'On',
      hintOn:             "You'll get one reminder in the evening if you haven't practiced that day. Turning this off stops reminders on all your devices.",
      statusOff:          'Off',
      hintOff:            "Turn on to get one reminder in the evening when you haven't practiced yet.",
      statusBlocked:      'Blocked',
      hintBlocked:        "Your browser is blocking notifications for CardSmith, so the switch can't do anything yet. Allow notifications for this site in your browser's settings — or for the CardSmith app in your device's notification settings — then come back and press it again.",
      statusUnsupported:  'Not available here',
      hintUnsupported:    "This browser can't do push notifications, so reminders won't work here. On iPhone and iPad they work only after adding CardSmith to the Home Screen.",

      // ── The account and this device disagree ──────────────────────────────
      hintAccountOnDeviceOff: 'Reminders are on for your account and are going to your other devices — but not to this one.',
      hintAccountOnNoDevice:  "Reminders are on for your account, but no device is set up to receive them, so nothing is arriving.",
      hintDeviceOnAccountOff: "This browser is set up for reminders, but your account has them switched off, so none are being sent.",
      actionEnableHere:       'Turn on for this device',
      actionFinish:           'Finish turning on',

      // ── What happened when it could not proceed ───────────────────────────
      busyOn:             'Turning on…',
      busyOff:            'Turning off…',
      actionRetry:        'Try again',
      issueDismissed:     'The permission request was closed before you answered it. Press the switch to ask again.',
      issueRefused:       "You just declined the request, so reminders can't be turned on. You can still allow notifications for this site in your browser's settings, then press the switch again.",
      issueBlocked:       "Notifications are blocked for this site, so nothing was asked. Allow them in your browser's settings first.",
      issueWorker:        "The app's background service didn't start, so reminders can't be set up here. Reload the page and try again.",
      issueKey:           "Reminders can't be set up right now — the server didn't return what's needed. Nothing was changed. Try again in a little while.",
      issueSubscribe:     'Your browser refused to set up reminders. Nothing was changed. Try again, or reload the page.',
      issueRegister:      "Your browser is ready, but we couldn't save this device to your account. Nothing was changed — press try again.",
      issueDisable:       "Couldn't turn reminders off. Nothing was changed. Try again.",
      issueStatus:        "Couldn't check what your account has, so this shows what this device holds only.",
    },
    devTools: {
      title:         'Dev tools',
      togglePro:     'Toggle Pro (dev)',
      currentPlan:   'Current plan:',
      switching:     'Switching…',
      switchToFree:  'Switch to Free',
      switchToPro:   'Switch to Pro',
    },
    languages: {
      title:            'Languages',
      interfaceTitle:   'Interface language',
      interfaceDesc:    'Language used for navigation, buttons, and labels.',
      // ADR-111 D5: the server refused to newly set a language that is not released yet.
      notReleased:      'That language isn’t available yet.',
      // The keep/revert prompt after an interface-language switch. ADR-111 D7:
      // it shows these in the new language, then in English.
      revert:           'Revert',
      keep:             'Keep',
      revertCountdown:  (s: number) => `Auto-reverting in ${s}s`,
    },
    nativeLanguage: {
      title:  'Native language',
      desc:   'The language you already speak. Used to show translations alongside cards for the language you’re learning.',
      choose: 'Choose a language…',
    },
    dailyGoal: {
      title:   'Daily goal',
      desc:    'How many study sessions you want to complete each day. One session = 40 cards.',
      saved:   'Saved',
      option1: '1 session · 40 cards',
      option2: '2 sessions · 80 cards',
      option3: '3 sessions · 120 cards',
    },
    translations: {
      sectionTitle: 'Translations',
      showTitle:    'Show word translations',
      notAvailable: 'Translations are not available for your current language pair.',
      showDesc:     'Show a translation in your native language alongside each card definition.',
      ariaToggle:   'Toggle word translations',
    },
    connected: {
      title:              'Connected accounts',
      desc:               'Link a provider to sign in faster. You can use either method once a provider is connected.',
      cannotConnect:      'Could not start sign-in.',
      cannotDisconnect:   'Cannot disconnect the only sign-in method.',
      couldNotDisconnect: 'Could not disconnect that account.',
      connected:          'Connected',
      disconnect:         'Disconnect',
      connect:            'Connect',
      loading:            'Loading…',
    },
  },

  // ── Admin tester-activity view ────────────────────────────────────────────
  adminActivity: {
    title:            'Tester Activity',
    subtitle:         'Who is studying, how consistently, and where people drop off — from database state only.',
    generatedAt:      (ts: string) => `As of ${ts}`,
    loading:          'Loading…',
    failed:           'Failed to load activity.',
    // Cohort summary
    cohortTitle:      'This week',
    totalUsers:       'Total users',
    activeThisWeek:   'Active this week',
    activeWindow:     (d: number) => `active in last ${d}d`,
    sessionsStarted:  'Sessions started',
    sessionsDone:     'Sessions completed',
    abandonRate:      'Abandoned mid-way',
    retentionTitle:   'Retention by time since registration',
    cohortCol:        'Registered',
    cohortSize:       'Users',
    activeCol:        'Active',
    // Per-user table
    perUserTitle:     'Per user',
    colUser:          'User',
    colRegistered:    'Registered',
    colOnboarded:     'Onboarded',
    colLastActive:    'Last active',
    colStreak:        'Streak',
    colSessions:      'Sessions',
    colClassic:       'Classic',
    colAnswers:       'Answers',
    colAccuracy:      'Accuracy',
    colMastery:       'Mastery ladder',
    colMastered:      'Mastered',
    yes:              'Yes',
    no:               'No',
    never:            'never',
    daysAgo:          (n: number) => (n === 0 ? 'today' : `${n}d ago`),
    classicOnlyNote:  'classic only',
    sessionsFmt:      (done: number, started: number) => `${done}/${started}`,
    // Worst cards
    worstTitle:       'Lowest-accuracy cards (group-wide)',
    worstHint:        'A card-quality signal, not a user signal. Cards with the most-missed answers across everyone.',
    colTerm:          'Term',
    colAttempts:      'Attempts',
    colCardAccuracy:  'Accuracy',
    noWorstCards:     'Not enough answered cards yet to surface a signal.',
    noUsers:          'No users yet.',
  },

  // ── Progress page ─────────────────────────────────────────────────────────
  progress: {
    title:         'Progress',
    wordsKnown:    'Words known',
    level:         (lv: string) => `Level ${lv}`,
    streak:        'Streak',
    personalBest:  'Personal best',
    best:          (n: number) => `Best ${n}d`,
    today:         'Today',
    todayFraction: (done: number, goal: number) => `${done} / ${goal} today`,
    todayCount:    (done: number) => `${done} today`,
    words:         'Words',
    grammar:       'Grammar',
    use:           'Use',
    comingSoon:    'Coming soon',
    vocabMap:      'Vocabulary map',
    mastery:       'Mastery',
    difficultWords: 'Difficult words',
    startLearning: 'Start learning to see your mastery breakdown.',
    masteredLabel: 'Mastered',
    masteredDesc:  'Levels 7-8',
    activeLabel:   'Active',
    activeDesc:    'Levels 4-6',
    fragileLabel:  'Fragile',
    fragileDesc:   'Levels 2-3',
    newLabel:      'New',
    newDesc:       'Level 1',
    noErrors:      'No errors recorded yet — keep studying!',
    trainNothingToPractise: 'Nothing to practise: these words are already mastered, and mastered words are not practised.',
    starting:      'Starting…',
    trainWords:    (n: number) => `Train these ${n} words`,
    loadError:     'Could not load progress',
    loadErrorSub:  'Check your connection and try again.',
    tryAgain:      'Try again',
    toLevel:       (lv: string) => `to ${lv}`,
    toVocabBand:   (lv: string) => `vocab ${lv}`,
    c2Mastered:    'Mastered',
    train:         'Train',
    trainTopic:    (topic: string) => `Train ${topic}`,
    loadingTopic:  (topic: string) => `Loading ${topic}`,
  },

  // ── Classic study page ────────────────────────────────────────────────────
  classicStudy: {
    back:           '← Back',
    retry:          'Retry',
    goBack:         '← Go back',
    loadingSession: 'Loading session…',
    loadingCard:    'Loading card…',
    know:           '✓ Know',
    dontKnow:       "🔴 Don't know",
    knowLabel:      'Know 🟢',
    dontKnowTitle:  "Don't know (←)",
    knowTitle:      'Know (→)',
    shuffle:        '🔀 Shuffle',
    swapDirection:  'Swap study direction',
    tapToFlip:      'tap to flip',
    kbHint:         "Space to flip · ← Don't know · → Know",
    swipeHint:      '← swipe to grade →',
    showAll:        'Show all',
    // ADR-104 Decision 7: stated on a face that has nothing to show, in place
    // of a blank card. Never card content, never filled from a dictionary.
    emptyTermFace:  'No term on this card',
    emptySideBFace: 'Nothing on this card besides the term',
    allDetails:     'All details',
    cardDisplay:    'Card display',
    close:          'Close',
    sideBParts:     'Side B parts',
    sideBHint:      'Tap to pin as slot 1, tap again to add slot 2. Blank = definition and translation.',
    loading:        'Loading…',
    couldNotLoadParts: 'Could not load parts.',
    noParts:        'This deck has no Side-B parts yet.',
    autoFirst:      'Auto — definition and translation',
    noCard:         'No card on screen',
    preview:        'Preview',
    saving:         'Saving…',
    save:           'Save',
    saved:          'Saved',
    defFirst:       'Def → Term',
    termFirst:      'Term → Def',
    // StudyComplete strings
    perfectRound:   'Perfect round!',
    roundComplete:  'Round complete',
    reviewed:       (n: number) => `You reviewed all ${n} cards`,
    knownLabel:     'Known',
    unknownLabel:   'Unknown',
    score:          'Score',
    studyUnknown:   (n: number) => `Study Unknown Only (${n})`,
    studyAgain:     (n: number) => `Study Again (all ${n})`,
    backToDeck:     'Back to Deck',
    // StudyProgress strings
    progressKnown:     (n: number) => `${n} known`,
    progressRemaining: (n: number) => `${n} remaining`,
    progressUnknown:   (n: number) => `${n} unknown`,
    // Flip-card reveal control (FlipCard's whole-card aria-label). Asymmetric
    // on purpose (front names the action, back does not) — preserved as found.
    flipAriaFront:  'Front of card — click to flip',
    flipAriaBack:   'Back of card',
    // MiniPreview part-type captions (settings sheet live preview) — not
    // content, a bracketed placeholder standing in for the actual media.
    imageCaption:   '[image]',
    audioCaption:   '[audio]',
    presentOnPercent: (pct: number) => `Present on ${pct}% of cards`,
  },

  // ── Library: Decks page ───────────────────────────────────────────────────
  library: {
    title:          'My Decks',
    deckCount:      (n: number) => `${n} deck${n !== 1 ? 's' : ''}`,
    folderCount:    (n: number) => `${n} folder${n !== 1 ? 's' : ''}`,
    lookup:         'Quick word lookup',
    selectDecks:    'Select decks',
    folders:        '📁 Folders',
    importText:     '📝 Import text',
    newDeck:        '+ New Deck',
    searchDecks:    'Search decks…',
    all:            'All',
    prev:           'Prev',
    next:           'Next',
    deselectAll:    'Deselect all',
    selectAll:      'Select all',
    selected:       (n: number) => `${n} selected`,
    tapToSelect:    'Tap decks to select',
    groupIntoCourse: 'Group into course',
    cancel:         'Cancel',
    tapDeck:        'Tap a deck to select',
    decksSelected:  (n: number) => `${n} deck${n !== 1 ? 's' : ''} selected`,
    paused:         'Paused',
    cards:          (n: number) => `${n} card${n !== 1 ? 's' : ''}`,
    due:            'due',
    learn:          'Learn',
    pauseTitle:     'Pause — exclude from daily study',
    resumeTitle:    'Resume — include in daily study',
    resume:         '▶ Resume',
    moreActions:    'More actions',
    deleteTitle:    (name: string) => `Delete ${name}`,
    confirmDelete:  (name: string) => `Delete "${name}"?`,
    allCardsDeleted: 'All cards will be deleted.',
    noDecksFound:   'No decks found',
    noDecksYet:     'No decks yet',
    noDecksMatch:   (q: string) => `No decks match "${q}"`,
    getStarted:     'Create your first deck to get started.',
    importFromText: '📝 Import from text',
    failedToLoad:   'Failed to load decks',
    serverError:    'Could not reach the server. Check your connection and try again.',
    retry:          'Retry',
    moveToFolder:   'Move to folder',
    noFolder:       'No folder',
    noFoldersYet:   'No folders yet',
    deleteBtn:      'Delete',
    createError:    'Could not create deck. Please try again.',
    moveError:      'Could not move deck.',
    tabMine:        'Mine',
    tabCatalog:     'Catalog',
    foldersLabel:   'Folders',
    sortNewest:     'Newest first',
    sortOldest:     'Oldest first',
    sortAZ:         'A → Z',
    sortZA:         'Z → A',
    sortMostDue:    'Most due',
    sortLabel:      'Sort',
    addContent:     'Add content',
    add:            'Add',
    clearSearch:    'Clear search',
    sortAndSelect:  'Sort and select options',
    noDecksInFolder: 'No decks in this folder',
    addFirstDeck:   'Add your first deck',
    addFirstDeckDesc: 'Create a deck, import text, or look up a word',
    couldNotLoadDecks: 'Could not load decks',
    checkConnection: 'Check your connection and try again.',
    prevPage:       '← Prev',
    nextPage:       'Next →',
    moreOptions:    'More options',
    moreOptionsFor: (name: string) => `More options for ${name}`,
    allDecks:       'All decks',
    manageFolders:  'Manage folders…',
    clearFolderFilter: 'Clear folder filter',
    deleteDeck:     'Delete deck',
    permanentlyDeleted: 'All cards will be permanently deleted.',
    renameDeck:     'Rename',
    renameSave:     'Save',
    renameLabel:    'Deck title',
    renameError:    'Could not rename deck.',
  },

  // ── New deck — short creation form (name + optional language only;
  // description / public / daily-queue moved to deck settings post-create) ──
  // ── Library → Mine: «Decks | Texts» (handoff §7, §19.5) ───────────────────
  textList: {
    switchLabel:   'Decks or texts',
    segDecks:      'Decks',
    segTexts:      'Texts',
    gaps:          (n: number) => `${n} gap${n !== 1 ? 's' : ''}`,
    noGaps:        'No gaps',
    deck:          (names: string) => `deck ${names}`,
    decks:         (names: string) => `decks ${names}`,
    noDeck:        'No deck',
    waiting:       (n: number) => `${n} word${n !== 1 ? 's' : ''} waiting for cards`,
    levelOf:       (max: number) => `of ${max}`,
    levelAria:     (level: number, max: number) => `Level ${level} of ${max}`,
    notPractised:  'not practised',
    none:          '—',
    gapBlank:      'gap',
    colText:       'Text',
    colDecks:      'Decks',
    colLevel:      'Level',
    colPractice:   'Practice',
    moreFor:       (title: string) => `More options for ${title}`,
    deleteText:    'Delete text…',
    deleteTitle:   'Delete text?',
    deleteBody:    'The text and its practice progress will be deleted.',
    deleteBodyOne: (deck: string) => `The text and its practice progress will be deleted. Cards in ${deck} stay.`,
    deleteBodyMany: (decks: string) => `The text and its practice progress will be deleted. Cards in ${decks} stay.`,
    quote:         (name: string) => `“${name}”`,
    deleteConfirm: 'Delete',
    deleting:      'Deleting…',
    cancel:        'Cancel',
    deleteFailed:  'Could not delete the text. Try again.',
    deleted:       'Text deleted',
    emptyTitle:    'No texts yet',
    emptyBody:     'Save an article or your own essay — and practise its words right in it.',
    emptyAdd:      'Add text',
    loadFailed:    'Could not load your texts',
    retry:         'Try again',
    deckTexts:     (n: number) => `${n} text${n !== 1 ? 's' : ''}`,
  },

  // ── Deck screen: «Cards | Texts» (handoff §8, §19.6) ───────────────────────
  deckTexts: {
    switchLabel:   'Cards or texts',
    segCards:      'Cards',
    segTexts:      'Texts',
    // The phone's row meta: how many of the text's distinct words have a card in this deck.
    wordsInDeck:   (k: number) => `${k} word${k !== 1 ? 's' : ''} from the text in the deck`,
    words:         (n: number) => `${n} word${n !== 1 ? 's' : ''}`,
    level:         (level: number, max: number) => `level ${level} of ${max}`,
    addText:       'Add text',
    emptyTitle:    'No texts in this deck yet',
    emptyBody:     'Save an article or your own essay — and practise its words right in it, with gaps. New words from the text will go into this deck.',
    loadFailed:    'Could not load this deck’s texts',
    retry:         'Try again',
    paneEyebrow:   'Text',
    open:          'Open',
    selectText:    'Select a text on the left',
    paneFailed:    'Could not load the text',
  },

  // ── Text screen (handoff §9, §16.2, §19.7) ─────────────────────────────────
  textScreen: {
    backDeck:        'Deck',
    decksEyebrow:    'Decks',
    levelEyebrow:    'Text level',
    levelNow:        'NOW',
    levelStep1:      'Word bank open',
    levelStep2:      'Bank on request — opening it is a hint',
    levelStep3:      'No bank',
    levelFooter:     'The level rises if you get through the text with no answers shown and without opening the bank. It never falls. Card levels do not change with practice.',
    waitingBefore:   (when: string) => `Today’s word limit for texts is used up. You can add them when it refreshes — ${when}.`,
    waitingAfter:    'The limit has refreshed — the words can be added.',
    addLater:        (when: string) => `Add ${when}`,
    addAll:          (k: number, deck: string) => `Add ${k} word${k !== 1 ? 's' : ''} to ${deck}`,
    addSome:         (m: number, k: number) => `Add ${m} of ${k}`,
    adding:          'Adding…',
    added:           (n: number, deck: string) => `${n} card${n !== 1 ? 's' : ''} added to ${deck}`,
    addFailed:       'Could not add the words. Try again.',
    notYet:          (when: string) => `These words can be added ${when}.`,
    loadFailed:      'Could not load the text',
    retry:           'Try again',
  },

  // ── Edit mode of a text (handoff §15) ──────────────────────────────────────
  textEdit: {
    screenTitle:     'Edit text',
    close:           'Close without saving',
    done:            'Done',
    tabsLabel:       'Sections',
    tabText:         'Text',
    tabWords:        'Words',
    tabDecks:        (n: number) => `Decks ${n}`,
    titleField:      'Title',
    bodyField:       'Text',
    stripRemoved:    (form: string) => `“${form}” is no longer in the text — the gap will go`,
    stripGrew:       (form: string, n: number) => `“${form}” is now in ${n} places — all become one gap`,
    stripPhrase:     (phrase: string) => `The phrase “${phrase}” changed — the gap will go`,
    stripEmpty:      'The text is empty',
    stripUndo:       'Undo',
    checking:        'Checking the gaps…',
    previewFailed:   'Could not check the gaps. Your edit is kept.',
    retry:           'Try again',
    tooLong:         (max: number) => `A text can be at most ${max} characters.`,
    tooMany:         (max: number) => `A text can have at most ${max} gaps.`,
    markWords:       'Mark words in the text',
    inTextOrder:     'In text order',
    noGaps:          'No gaps yet. Mark words in the text to practise them.',
    inPlaces:        (n: number) => `in ${n} places`,
    gapMenu:         (form: string) => `More for the gap “${form}”`,
    removeGap:       'Remove gap',
    formEyebrow:     'Form',
    formUnset:       'Form not set',
    formHidden:      'Form is not shown',
    change:          'Change',
    specify:         'Set',
    formInText:      'Form in the text',
    loadingOptions:  'Loading…',
    optionsFailed:   'Could not load the forms.',
    hideForm:        'don’t show the form',
    customPlaceholder: 'Or your own label…',
    customCount:     (n: number, max: number) => `${n} / ${max}`,
    cancel:          'Cancel',
    saveForm:        'Save form',
    resetForm:       'Reset',
    dictionary:      (base: string, translation: string | null) => `Dictionary: ${base}${translation ? ` — ${translation}` : ''}`,
    acceptedEyebrow: 'Accepted',
    noAccepted:      'No accepted answers',
    removeAnswer:    (answer: string) => `Remove answer ${answer}`,
    addAnswerLabel:  (form: string) => `Accept another answer for “${form}”`,
    addAnswerPlaceholder: 'Another answer',
    addAnswer:       'Add',
    answerExists:    'That answer is already accepted.',
    answerIsForm:    'That is the gap’s own answer.',
    newGapNote:      'The form can be set once the text is saved.',
    waitingEyebrow:  'Waiting to become cards',
    waitingHint:     'Words that did not fit today’s limit. Tap × to drop one from the queue.',
    dropWaiting:     (word: string) => `Do not add ${word}`,
    decksIntro:      'The text is listed under “Texts” of every linked deck. Unlinking does not touch cards.',
    unlink:          'Unlink',
    linkDeck:        'Link a deck',
    noDeckChip:      'No deck',
    noDeckFootnote:  'If you unlink all decks, the text stays in Library → Texts marked “No deck”.',
    deckMeta:        (n: number) => `${n} card${n === 1 ? '' : 's'}`,
    pickerTitle:     'Link a deck',
    pickerLinked:    'Linked',
    pickerLink:      'Link',
    pickerFootnote:  'Linking creates no cards. To turn words into cards, mark them: “Words” → “Mark words in the text”.',
    saveTitle:       'Save changes?',
    saveConfirm:     'Save',
    saveBack:        'Back to editing',
    saving:          'Saving…',
    saveFailed:      'Couldn’t save. Check your connection and try again.',
    lineTitle:       (title: string) => `Title: “${title}”.`,
    lineBody:        'The text was changed.',
    lineGapRemoved:  (form: string) => `Gap “${form}” will go — the word is no longer in the text.`,
    lineGapDropped:  (form: string) => `Gap “${form}” removed. The word stays in the text.`,
    lineGapNew:      (form: string) => `New gap “${form}”.`,
    linePlaces:      (form: string, n: number) => `“${form}” is now in ${n} place${n === 1 ? '' : 's'} — all become one gap.`,
    lineDeckUnlinked: (deck: string) => `Deck ${deck} unlinked. Its cards stay.`,
    lineDeckLinked:  (deck: string) => `Deck ${deck} linked.`,
    lineAnswerRemoved: (answer: string, form: string) => `Accepted answer “${answer}” for “${form}” removed.`,
    lineAnswerAdded: (answer: string, form: string) => `Accepted answer “${answer}” added for “${form}”.`,
    lineLabel:       (form: string, label: string) => `Form of “${form}”: ${label}.`,
    lineLabelHidden: (form: string) => `The form of “${form}” is not shown.`,
    lineLabelReset:  (form: string) => `The form of “${form}” goes back to the automatic one.`,
    lineWaiting:     (word: string) => `“${word}” will not be added as a card.`,
    discardTitle:    'Leave without saving?',
    discardBody:     'Your changes will be lost.',
    discardConfirm:  'Leave without saving',
    discardStay:     'Back to editing',
    pickerScreenTitle: 'Mark words',
    pickerBack:      'Edit',
    menuEdit:        'Edit',
    // Card marks in edit mode (ADR-115 Amendment 3). Removing a mark never reads as deleting a card.
    unmark:          'Unmark',
    unmarked:        (w: string) => `Unmarked “${w}”`,
    unmarkCard:      (w: string) => `Unmark ${w}`,
    cardedIn:        (deck: string) => `“${deck}” already has this card, so it can only be a gap.`,
    cardedLegend:    (deck: string) => `Words underlined in grey already have a card in “${deck}”.`,
    cardsEyebrow:    (n: number) => `New cards · ${n}`,
    cardsHint:       'They become cards when you save this edit. Unmark them here or in the text.',
    cardsTo:         (deck: string) => `To the deck “${deck}”.`,
    cardsToNew:      (title: string) => `To a new deck, “${title}”. The text will be linked to it.`,
    cardsDeckQuestion: 'Where should the cards go',
    cardsAmongHint:  'The text is linked to several decks. Choose one.',
    cardsLinkNote:   'The text will be linked to this deck.',
    cardsChecking:   'Checking the daily limit…',
    cardsCheckFailed: 'Couldn’t check the daily limit. You can still save; the limit is checked when you do.',
    cardsAccepted:   (n: number) => `Will become cards: ${n}`,
    cardsSkipped:    (deck: string) => `Already in “${deck}”, so no new cards:`,
    cardMarksInvalid: 'Some marked words changed in the text, so their marks were removed. Check and save again.',
    cardDeckNeeded:  'Choose a deck for the cards.',
    // The receiving deck as the learner's choice (ADR-115 Amendment 4).
    cardsOtherDeck:  'Another deck',
    cardsNewDesc:    'Named after the text; you can change it.',
    newDeckName:     'Deck name',
    deckReceives:    'New cards go here',
    deckNewMeta:     'Made when you save',
    lineDeckNew:     (deck: string) => `A new deck, “${deck}”, will be made and linked.`,
  },

  // ── The text player (handoff §9–§14, §18) ──────────────────────────────────
  textPlayer: {
    close:           'Close',
    reading:         'Reading',
    levelEyebrow:    (max: number) => `Text level · of ${max}`,
    readLevel1:      'When filling, the word bank will be open.',
    readLevel2:      'The word bank is hidden. You can open it — that counts as a hint.',
    readLevel3:      'No word bank — just you and the text.',
    readCount:       (n: number) => `${n} gap${n === 1 ? '' : 's'}.`,
    readRepeat:      'A word that appears in two places is one gap.',
    fill:            'Fill in the gaps',
    stripMeta:       (level: number, left: number) => `Lv. ${level} · ${left} left`,
    placeholder:     'Word from the text',
    answerLabel:     'Your answer',
    check:           'Check',
    prevGap:         'Previous gap',
    nextGap:         'Next gap',
    bankLabel:       'Word bank',
    openBank:        'Open the word bank',
    bankHint:        'counts as a hint',
    bankPrefix:      'hint ·',
    toGap:           'To the gap',
    formEyebrow:     'Form',
    repeatWithLabel: (n: number) => `In the text ${n} times — one answer fills every place.`,
    noLabel:         'Type the word in the form it has in the text.',
    noLabelRepeat:   (n: number) => `This word is in the text ${n} times — one answer fills every place.`,
    correct:         'Correct.',
    fits:            (letter: string) => `Right meaning, but another word is needed here — it starts with “${letter}”.`,
    wrong:           (letter: string) => `Not that. First letter: “${letter}”.`,
    revealed:        (answer: string) => `Answer shown: “${answer}”. ✓ to go on.`,
    myAnswerFits:    'My answer fits',
    bankOpened:      'Bank opened — that is a hint: the level will not rise this time.',
    resumed:         'Carrying on where you left off.',
    revisiting:      'Already filled. ↓ for the next gap.',
    allDone:         'All gaps are filled.',
    summary:         'Summary',
    checkFailed:     'Could not check the answer. Try again.',
    leaveTitle:      'Leave practice?',
    leaveBody:       (a: number, b: number) => `Filled ${a}, ${b} left. Your answers and hints are kept — you can carry on from this gap, from the text screen.`,
    leaveStay:       'Keep practising',
    leaveGo:         'Leave',
    doneTitle:       'Text completed',
    doneSub:         (title: string, n: number) => `${title} · ${n} gap${n === 1 ? '' : 's'}`,
    statFirst:       'First time',
    statFirstNote:   'No hints',
    statHinted:      'With a hint',
    statHintedNote:  'A first letter or “fits, but not that”',
    statRevealed:    'Answer shown',
    statRevealedNote: 'After a second mistake',
    levelEyebrowDone: 'Text level',
    newLevel:        'new level',
    unchanged:       'unchanged',
    maxLevel:        'maximum',
    rose1:           'Next time the word bank will be hidden. You can open it — but that is a hint.',
    rose2:           'Next time there will be no word bank.',
    stayedIntro:     (next: number) => `To reach level ${next}, complete the text like this:`,
    maxText:         'This is the text’s highest level.',
    condNoReveal:    'Not a single answer shown',
    condBankNotOpened: 'The word bank was not opened',
    condBankOk:      'Don’t open the word bank — done',
    condBankOpened:  'Don’t open the word bank — opened this time',
    condRevealOk:    'No answers shown — done',
    condRevealCount: (n: number) => `No answers shown — ${n} this time`,
    practiceNote:    'This is practice: your card levels have not changed.',
    again:           'Practise again',
    viewText:        'View the text',
    done:            'Done',
    loadFailed:      'Could not load the practice.',
    retry:           'Try again',
    train:           'Practise',
    resume:          'Continue',
    restart:         'Start again',
    restartTitle:    'Start again?',
    restartBody:     (a: number, n: number) => `This attempt has ${a} of ${n} gaps filled. The answers will be deleted and practice will start with reading.`,
    restartKeep:     (level: number, max: number) => `The text level (${level} of ${max}) and the accepted answers are kept.`,
    restartConfirm:  'Start again',
    restartStay:     'Continue the attempt',
    statusStarted:   (when: string, a: number, n: number) => `started ${when} · ${a} of ${n}`,
    statusStartedLong: (when: string, a: number, b: number) => `started ${when}: ${a} filled, ${b} left`,
    statusCompleted: (k: number) => `completed ${k} time${k === 1 ? '' : 's'}`,
    today:           'today',
    yesterday:       'yesterday',
    backToText:      'Text',
    deskSubtitleReading: (level: number) => `Practice · level ${level}`,
    deskSubtitle:    (level: number, left: number) => `Practice · level ${level} · ${left} left`,
    deskSubtitleDone: 'Practice completed',
    deskHint:        'Enter — check · ↑ ↓ in the answer field — neighbouring gap · click a gap — go to it',
    paneTrain:       'Practise the text',
    paneNote:        'Card levels do not change from practice',
  },

  // ── A shared text: the link, the page it opens, the copy (handoff §17) ─────
  textShare: {
    share:           'Share',
    sheetTitle:      'Share this text',
    intro:           'With the link, anyone can read the text and practise it, and signed in to CardSmith, save a copy of their own.',
    seeTitle:        'What the link shows',
    seeBody:         'The title, the text and its gaps.',
    hiddenLine:      'What the link does not show — your decks and cards, answers, accepted answers and the text’s level.',
    copyNote:        'A saved copy gets the gaps with their labels and accepted answers.',
    create:          'Create a link',
    creating:        'Creating…',
    cancel:          'Cancel',
    close:           'Close',
    enabled:         (date: string) => `Link access is on · created ${date}`,
    linkLabel:       'Link to the text',
    copy:            'Copy',
    copied:          'Copied',
    send:            'Send…',
    newLink:         'Create a new link…',
    newTitle:        'Create a new link?',
    newBody:         'The old link will stop working. Copies others have already saved stay theirs.',
    newConfirm:      'Create a new one',
    revoke:          'Turn off the link…',
    revokeTitle:     'Turn off link access?',
    revokeBody:      'The link will stop working. Copies others have already saved stay theirs.',
    revokeNote:      'You can create a new link at any time — it will be a different one.',
    revokeConfirm:   'Turn off',
    revoked:         'Link access turned off',
    failed:          'That didn’t work. Check your connection and try again.',
    home:            'Home',
    eyebrow:         'Shared text',
    note:            'You can practise right away. The text’s level and your answers are kept only if you save the text.',
    saveCta:         'Save to my texts',
    saveTitle:       'Save to my texts',
    saveBody:        (n: number) => `Your own copy is saved: the text and ${n} gap${n === 1 ? '' : 's'}. You can change it — the original stays as it is.`,
    noDeck:          'No deck',
    noDeckSub:       'The text will be in Library → Mine → Texts.',
    newDeck:         'New deck',
    newDeckSub:      'Gets the text’s title. Rename it later.',
    toDeck:          'In a deck',
    toDeckSub:       'Choose one of your decks',
    noDecksYet:      'You have no decks yet',
    saveCaption:     'No cards are created.',
    saveConfirm:     'Save',
    saving:          'Saving…',
    saved:           'Saved to your texts',
    alreadyCopy:     'You already have this text — your copy is open.',
    deadTitle:       'This link no longer works',
    deadBody:        'The link does not open: access to the text was turned off or the link was replaced by a new one. If you saved this text, the copy is in your texts.',
    deadMine:        'To my texts',
    deadHome:        'To the home page',
    loadFailed:      'Could not open the text',
    retry:           'Try again',
    stripLeft:       (n: number) => `${n} left`,
    deskSubtitle:    (n: number) => `Practice without saving · ${n} left`,
    deskSubtitleDone: 'Practice finished',
    viewerLeaveBody: 'Without saving, answers are not kept: next time practice starts from the beginning.',
    notSaved:        'Not saved',
    notSavedBody:    'Without saving, practice always runs with the word bank open. Save the text and its level starts rising from 1.',
    done:            'Done',
  },

  deckCreate: {
    title:          'New Deck',
    closeAria:      'Close',
    nameLabel:      'Deck name',
    required:       '*',
    namePlaceholder: 'e.g. Spanish Vocabulary',
    languageLabel:  'Deck language',
    cancelBtn:      'Cancel',
    creating:       'Creating…',
    createBtn:      'Create Deck',
  },

  // ── Library: Courses page ─────────────────────────────────────────────────
  courses: {
    title:         'My Courses',
    courseCount:   (n: number) => `${n} course${n !== 1 ? 's' : ''}`,
    newCourse:     '+ New Course',
    isPublic:      'Public',
    deckCount:     (n: number) => `${n} deck${n !== 1 ? 's' : ''}`,
    folderCount:   (n: number) => `${n} folder${n !== 1 ? 's' : ''}`,
    learnAll:      '🎯 Learn All',
    confirmDelete: (name: string) => `Delete "${name}"?`,
    decksKept:     "Decks won't be deleted.",
    noCoursesYet:  'No courses yet',
    noCoursesDesc: 'Group your decks into a course to study them together.',
    createNew:     '+ New Course',
    createTitle:   'New Course',
    titleLabel:    'Title',
    required:      '*',
    titlePlaceholder: 'e.g. Spanish A1 Course',
    descLabel:     'Description',
    descPlaceholder: 'Optional…',
    makePublic:    'Make public',
    cancelBtn:     'Cancel',
    creating:      'Creating…',
    createBtn:     'Create',
    prev:          '← Prev',
    next:          'Next →',
    deleteBtn:     'Delete',
    cancel:        'Cancel',
  },

  // ── Explore page ──────────────────────────────────────────────────────────
  explore: {
    title:       'Explore',
    subtitle:    'Browse public decks and courses from the community.',
    searchPlaceholder: 'Search community content…',
    search:      'Search',
    by:          (author: string) => `by ${author}`,
    inLibrary:   'In Library',
    cardCount:   (n: number, extra: string) => `${n} cards${extra}`,
    signUp:      'Sign up',
    toAddDeck:   'to add this deck to your library.',
    preview:     'Preview',
    openLibrary: 'Open in Library',
    addLibrary:  'Add to Library',
    adding:      'Adding…',
    decksTab:    'decks',
    coursesTab:  'courses',
    viewCourse:  'View Course',
    loadMore:    'Load more',
    noResults:   'No results found',
    noMatch:     (q: string) => `Nothing matches "${q}"`,
    noContent:   'No community content available yet.',
    anonymous:   'Anonymous',
  },

  // ── Catalog page ──────────────────────────────────────────────────────────
  catalog: {
    title:        'Catalog',
    // Phone-only body heading (CatalogPage's <h1>, mobile breakpoint only —
    // desktop's identity is the screen-header bar's "Library", never both;
    // see MineDeckContent's `lib.title` for the sibling tab's identical
    // pattern). Deliberately not `title`: the tab already says "Catalog"
    // (where you are); this says what the surface holds, the job the
    // subtitle below it already does at every width.
    heading:      'Decks & Courses',
    subtitle:     'Curated English decks and courses for structured learning.',
    decksTab:     'Decks',
    coursesTab:   'Courses',
    exploreTab:   'Explore',
    clearFilter:  'Clear',
    noDecks:      'No decks found',
    noSystemDecks: 'No system decks available yet.',
    coursesComingSoon: 'System courses coming soon',
    coursesComingSoonDesc: 'Hand-authored CEFR courses with structured lessons and grammar will appear here.',
    coursesComingSoonHint: (tab: string) => `In the meantime, use the ${tab} tab to start learning curated vocabulary.`,
    decksTabHint:  'Decks',
    browseBtn:    'Browse',
    availableIn:  (lang: string) => `Available in ${lang} only`,
    continueBtn:  'Continue',
    previewBtn:   'Preview',
    loadError:    'Could not load content. Please try again.',
    decks:        'decks',
    cards:        'cards',
    topics:       'topics',
    searchCommunity: 'Search community decks…',
    noMatchCommunity: (q: string) => `No community decks match "${q}".`,
    noCommunity:  'No community decks yet.',
    noLevelDecks: (level: string) => `No ${level} decks available yet.`,
    prev:         '← Prev',
    next:         'Next →',
    switchLangTo: (label: string) => `Switch explanations to ${label}?`,
    switching:    '…',
    switchBtn:    'Switch',
    cancel:       'Cancel',
    loadMore:     'Load more',
    anonymous:    'Anonymous',
    inLibrary:    'In Library',
    adding:       'Adding…',
    add:          'Add',
    // CatalogCourseDetailPage strings
    enrolled:          'Enrolled',
    courseContent:     'Course Content',
    noContent:         'No content yet.',
    sampleCards:       (deckTitle: string) => `Sample Cards — ${deckTitle}`,
    enrollBtn:         'Enroll',
    unenrollBtn:       'Unenroll',
    enrollToSee:       'Enroll to see full card content',
    courseNotFound:    'Course not found',
    courseRemovedDesc: "This course may have been removed or you don't have access.",
    backToCatalog:     'Back to Catalog',
    deckCount:         (n: number) => `${n} deck${n !== 1 ? 's' : ''}`,
    cardCount:         (n: number) => `${n} card${n !== 1 ? 's' : ''}`,
  },

  // ── CSV import modal ──────────────────────────────────────────────────────
  csvImport: {
    title:                 'Import CSV',
    mapColumnsTitle:       'Map Columns',
    importCompleteTitle:   'Import Complete',
    noHeaderError:         'CSV has no header row — check the file and try again',
    onlyCsvSupported:      'Only .csv files are supported',
    importFailedFallback:  'Import failed. Check file format and try again.',
    targetDeckLabel:       'Target Deck',
    createNewDeck:         '+ Create new deck',
    deckNamePlaceholder:   'Deck name…',
    deckNameRequired:      'Deck name is required',
    addToQueueToggle:      'Add to daily queue',
    csvFileLabel:          'CSV File',
    dropHere:              'Drop your CSV here',
    dragOrClick:           'Drag a .csv file here, or click to browse',
    tapToChoose:           'Tap to choose a .csv file',
    columnMappingNext:     'Column mapping step follows automatically',
    cancel:                'Cancel',
    changeFile:            'Change',
    emptyCellPlaceholder:  'empty',
    noDataRows:            'No data rows in file',
    columnsWillBeImported: (n: number) => `${n} column${n !== 1 ? 's' : ''} will be imported`,
    columnsSkipped:        (n: number) => `${n} skipped`,
    backBtn:               '← Back',
    importing:             'Importing…',
    importBtn:             'Import',
    deckUpdated:           'Your deck has been updated.',
    importedLabel:         'Imported',
    skippedLabel:          'Skipped',
    errorsLabel:           (n: number) => `Errors (${n})`,
    importAnother:         'Import Another',
    doneBtn:               'Done',
    termRequired:          'Assign exactly one column as Term',
    termTooMany:           'Only one column can be Term',
    defRequired:           'Assign exactly one column as Definition',
    defTooMany:            'Only one column can be Definition',
    roleSkip:              'Do not import',
    roleTerm:              'Term',
    roleDefinition:        'Definition',
    roleExample:           'Example',
    roleTranslation:       'Translation',
    roleImage:             'Image',
    roleCustom:            'Custom…',
    // Parsing determinations — each is a visible, changeable default
    parsedAs:              'Read as',
    encodingLabel:         'Encoding',
    delimiterLabel:        'Separator',
    delimiterComma:        'Comma  ,',
    delimiterSemicolon:    'Semicolon  ;',
    delimiterTab:          'Tab',
    headerRowToggle:       'First row is a header',
    headerRowOnHint:       'The first row names the columns and is not imported.',
    headerRowOffHint:      'The first row is imported as data.',
    reparsing:             'Re-reading file…',
    rowCount:              (n: number) => `${n} row${n !== 1 ? 's' : ''} to import`,
    columnLabel:           (n: number) => `Column ${n}`,
    customNamePlaceholder: 'Field name…',
    customNameRequired:    (col: string) => `${col}: enter a name for the custom field`,
    termRequiredNamed:     'Assign one column as Term',
    termTooManyNamed:      (cols: string) => `Only one column can be Term — currently ${cols}`,
    noPromptMaterialNotice:
      'No column is assigned to Definition or Translation. These cards will be created, but will not enter learning until one of those fields is filled — you can fill them from the deck afterwards.',
    parseFailedTitle:      'This file could not be read as a table',
    parseFailedRows:       (n: number, rows: string) =>
      `${n} row${n !== 1 ? 's' : ''} disagree on the number of fields (row${n !== 1 ? 's' : ''} ${rows}).`,
    parseFailedTryDelimiter: 'Try a different separator below.',
    encodingFailed:        'This file is not UTF-8 or Windows-1251. Re-save it as UTF-8 and upload it again.',
    importIntoDeck:        (title: string) => `Import into "${title}"`,
  },

  // ── Text import page ──────────────────────────────────────────────────────
  textImport: {
    title:           'Import Text',
    closeAria:       'Close',
    pasteLabel:      'Paste or type text',
    urlLabel:        'Paste a URL',
    pasteTabLabel:   'Text',
    urlTabLabel:     'URL',
    fileTabLabel:    'File',
    placeholder:     'Paste an article, lyrics, or any text in your target language…',
    urlPlaceholder:  'https://example.com/article',
    charLimit:       (used: number, max: number) => `${used} / ${max}`,
    extractBtn:      'Extract vocabulary',
    extracting:      'Extracting…',
    cancel:          'Cancel',
    fileDropZone:    'Drop a file here, or click to browse',
    fileHint:        'Supports PDF, DOCX, PPTX, plain text, and image files',
    scanning:        'Scanning…',
    noWordsFound:    'No new vocabulary found in this text.',
    loadError:       'Failed to load.',
    retry:           'Retry',
    heroTitle:       'Turn any text into flashcards',
    heroDesc:        'Paste an article, a song, a book chapter — CardSmith finds new words and builds a deck for you automatically.',
    addingTo:        'Adding to:',
    selectedDeckFallback: 'Selected deck',
    createNewDeckInstead: 'Create new deck instead',
    createNewDeck:   '+ Create new deck',
    addToExisting:   'Add to existing deck',
    ocrHint:         'Text from photo — review for recognition errors before extracting.',
    dismissOcrHint:  'Dismiss OCR hint',
    removeFile:      'Remove loaded file',
    clearText:       'Clear text',
    yourText:        'Your text',
    textareaPlaceholder: 'Paste an article, URL, song, or book chapter…',
    overLimit:       (n: number) => `${n.toLocaleString()} characters over the limit — please trim your text.`,
    startFromPhoto:  'Start from a photo',
    pasteImage:      (key: string) => `Or paste an image (${key})`,
    takePhoto:       'Take photo',
    fromGallery:     'From gallery',
    ocrDownloading:  'Loading language data…',
    ocrReading:      'Reading image…',
    ocrBeta:         'OCR · Beta · Lower accuracy on non-Latin scripts',
    ocrRetry:        'Try again',
    fetchAndExtract: 'Fetch & extract words',
    fetchingArticle: 'Fetching article…',
    extractWords:    'Extract words',
    extractingWords: 'Extracting words…',
    selectDeck:      'Select a deck…',
    searchDecksPlaceholder: 'Search decks…',
    loadingDecks:    'Loading…',
    noDecksFoundPicker: 'No decks found',
    dropAreaLabel:   'Drop a file or click to browse',
    dropHereLabel:   'Drop here',
    extractingFile:  'Extracting text from file…',
    readingFile:     'Reading file…',
    clickRetry:      'Click to try again',
    deckTitleLabel:  'Deck title',
    deckAutoGenerate:'Auto-generate',
    deckAddTitle:    '+ Add deck title',
    deckAddTitleHint:'(optional, auto-generates from text)',
    footerHint:       'Words you already know are filtered out automatically.',
    articleFetched:   'Article will be fetched automatically',
    dropOrBrowse:     'Drop a file or',
    dropBrowseLink:   'browse',
    uploadFile:       'Upload file',
    ctaReasonEmpty:   'Paste some text to continue',
    ctaReasonOverLimit: 'Text is too long — shorten it',
    ctaReasonNoDeck:  'Select a deck to add cards to',
    orImportFrom:    'or import from',
    tileScanPage:    'scan a page',
    tilePickImage:   'pick an image',
    tileFileFormats: '.pdf .docx .txt .csv',
    // File-upload errors (POST /text-import/upload/). One message per backend
    // code — the server's own `detail` text is English and never shown. None
    // suggests sending the same file again: for a broken file that never works.
    fileEmpty:            'This file is empty. Choose a different file.',
    uploadErrNoText:      (camera: string, gallery: string) =>
      `This file has no text that can be read — it may be a scan or made of images. To import it, photograph the pages (“${camera}”) or pick images of them (“${gallery}”).`,
    uploadErrCorrupted:   (format: string) =>
      `This file is damaged or isn't a valid ${format} file, so its text can't be read. Re-save or re-export it, or choose a different file.`,
    uploadErrEncrypted:   'This file is password-protected. Remove the password, save the file, and upload it again.',
    uploadErrUnsupported: "This file type isn't supported. Use .pdf, .docx, .pptx, .txt or .csv.",
    uploadErrTooLarge:    'This file is larger than 10 MB. Choose a smaller file.',
    uploadErrRateLimited: "You've uploaded a lot of files in the last hour. Wait a while before uploading another.",
    uploadErrGeneric:     "Couldn't extract text from this file.",
    uploadErrNetwork:     "Couldn't reach the server. Check your connection.",
    // Actions on the file and photo error banners. A file that can only fail
    // again (empty, too large, wrong type) offers another file, not a retry.
    chooseOtherFile:      'Choose a different file',
    chooseOtherImage:     'Choose a different image',
    fileReadFailed:       "Couldn't read this file. Try again.",
    deckTitlePlaceholder: 'e.g. Le Monde — Politics',
    // Photo recognition errors — raised in the browser, keyed by kind.
    ocrErrTooLarge:       (mb: number) => `This image is too large (${mb.toFixed(1)} MB). The maximum is 10 MB.`,
    ocrErrNoText:         "Couldn't find any text in this image. Try a clearer photo or better lighting.",
    ocrErrDecode:         "This image can't be opened. Choose a different one.",
    ocrErrGeneric:        "Couldn't recognise the text. Try a clearer image.",
    // Text and URL import errors (POST /text-import/extract/), one per backend code.
    extractErrBlockedUrl:        "That link can't be opened.",
    extractErrPageTooLarge:      'The page is too large (over 5 MB). Paste the text directly instead.',
    extractErrTimeout:           'The page took too long to load. Try again or paste the text directly.',
    extractErrNotHtml:           "That link doesn't lead to a web page. Paste the text directly instead.",
    extractErrPaywall:           "Couldn't get readable text from that page — it may be behind a paywall. Paste the text directly instead.",
    extractErrFetchFailed:       "Couldn't open that link. Check the address or paste the text directly.",
    extractErrTooShort:          "There isn't enough text at that link to import. Paste the text directly instead.",
    extractErrRedirectLoop:      'That link redirects too many times — the page may require signing in. Paste the text directly instead.',
    extractErrVideoUnavailable:  'This video is unavailable (it may be private, deleted or age-restricted). Paste the transcript text directly instead.',
    extractErrNoSubtitles:       'This video has subtitles turned off. Paste the transcript text directly instead.',
    extractErrNoSubtitleLanguage:"This video has no subtitles in the language you're learning. Try a different video or paste the text directly.",
    extractErrRateLimited:       'Too many requests. Wait a while and try again.',
    extractErrUnavailable:       'Text processing is temporarily unavailable. Try again later.',
    extractErrGeneric:           "Couldn't process this text. Try again.",
  },

  // ── Smart queue page ──────────────────────────────────────────────────────
  smartQueue: {
    building:   'Building your session…',
    allCaughtUp: 'All caught up!',
    noDueDesc:  'Come back later or import new words.',
    home:       '← Home',
    error:      'Something went wrong',
    errorDesc:  'Could not build your session. Check your connection and try again.',
    retry:      'Try again',
  },

  // ── Text Import Status page ───────────────────────────────────────────────
  textImportStatus: {
    statusAnalysing:  'Analysing text…',
    statusReviewing:  'Preparing word list…',
    statusGenerating: 'Generating cards…',
    statusDone:       'All done!',
    statusFailed:     'Generation failed',
    statusStarting:   'Starting…',
    noImportId:       'No import ID found. Please start from the text import page.',
    buildingDeck:     'Building your deck',
    deckReady:        'Deck ready',
    openingDeck:      'Opening your deck…',
    deckPartial:      'Deck partially created',
    addingToDeck:     (deck: string) => `Adding to "${deck}"`,
    cardsGenerated:   (n: number) => `${n} card${n !== 1 ? 's' : ''} generated successfully`,
    cardsProgress:    (cur: number, tot: number | string) => `${cur} / ${tot || '?'}`,
    cardsPartial:     (cur: number, tot: number, failed: number) =>
      `${cur} of ${tot} card${tot !== 1 ? 's' : ''} created — ${failed} failed to generate`,
    progressAriaLabel:'Card generation progress',
    generatingDesc:   'CardSmith is generating definitions, examples, and translations for each word. This usually takes under a minute.',
    deckReadyStudy:   'Your deck is ready to study!',
    startLearning:    'Start learning',
    partialFailDesc:  (failed: number, cur: number) =>
      `${failed} word${failed !== 1 ? 's' : ''} could not be generated, but your deck is ready with the ${cur} card${cur !== 1 ? 's' : ''} that succeeded.`,
    studyNCards:      (n: number) => `Study ${n} card${n !== 1 ? 's' : ''}`,
    backToImport:     'Back to import',
    errorWentWrong:   'Something went wrong',
    startOver:        'Start over',
    retryBtn:         'Try again',
    serverError:      'The server ran into a problem while generating your cards.',
    nothingToAddTitle:'Nothing new to add',
    nothingToAddDesc: (n: number) =>
      `All ${n} word${n !== 1 ? 's' : ''} you selected already have a card here. Nothing was changed.`,
    nothingToAddPlain:'Every word you selected already has a card here. Nothing was changed.',
    openDeck:         'Open deck',

    // ── Cards created without content (ADR-078) ────────────────────────────
    // Every confirmed word becomes a card; some arrive empty because the
    // service could not describe them. The headline says the deck exists, the
    // count says how much of it is waiting, and the reason line says why —
    // three different sentences for three different causes, because "the
    // service was busy" is not the same news as "the service is down".
    deckReadyPending: 'Deck ready — some cards need content',
    cardsCreated:     (n: number) => `${n} card${n !== 1 ? 's' : ''} created`,
    cardsWaiting:     (n: number) =>
      `${n} of them ${n !== 1 ? 'are' : 'is'} waiting for content`,
    reasonRateLimited:
      'The word service hit its limit for the moment, so it stopped partway. Nothing was lost — the words are here as cards. You can fill them in shortly.',
    reasonUnavailable:
      "The word service didn't answer, so those cards were created empty. Nothing was lost — you can fill them in from the deck.",
    reasonIncomplete:
      "The word service answered but couldn't describe those words. They're here as cards — you can fill them in from the deck, or write them yourself.",
    reasonUnknown:
      'Those cards were created empty. Nothing was lost — you can fill them in from the deck.',
    // The affordance is named, not automated: filling is something the person
    // starts. Never promise the app will finish on its own.
    fillHint:         'Open the deck and use Fill on a card to complete it.',

    // ── Words already in this deck (ADR-078 amendment) ─────────────────────
    // Information, not an error. Nothing went wrong and there is nothing to
    // retry: the cards for these words are already in this deck, which is
    // precisely why no second one was made. Worded so it reads as an account
    // of what happened, never as a failure the reader has to act on — no
    // "couldn't", no "failed", no apology.
    //
    // It says THIS deck, not "your other decks": a skip can now only mean the
    // deck being added to (a new deck is empty, and a word held elsewhere is
    // no longer a reason to refuse anything). Naming the other deck is
    // deliberately not offered — the reader is told a count and a reason, not
    // given a report on their library.
    skippedAlreadyInDeck: (n: number) =>
      `${n} word${n !== 1 ? 's' : ''} ${n !== 1 ? 'were' : 'was'} already in this deck, so ${n !== 1 ? 'they' : 'it'} ${n !== 1 ? 'were' : 'was'} not added again.`,
    // Fallback for a reason code this build doesn't know. Still states the
    // count — that is the part that closes the gap between what was picked and
    // what exists, and going silent about it is the failure this replaced.
    skippedGeneric: (n: number) =>
      `${n} word${n !== 1 ? 's' : ''} ${n !== 1 ? 'were' : 'was'} already in this deck and ${n !== 1 ? 'were' : 'was'} not added again.`,
  },

  // ── Word Review page ──────────────────────────────────────────────────────
  wordReview: {
    noDataFound:      'No word data found. Please start from the text import page.',
    goToImport:       'Go to text import',
    noNewWordsTitle:  'No new words found',
    allWordsKnown:    (n: number) => `All ${n} words in this text are already in your vocabulary. Great job!`,
    noVocabExtracted: "CardSmith couldn't extract any vocabulary from this text. Try a longer passage.",
    importAnother:    'Import another text',
    statFound:        'found',
    statKnown:        'already known',
    statBelow:        'below your level',
    statNew:          'new',
    showingForLevel:  'Showing words ranked for your level:',
    autoCaptionsWarn: 'These subtitles were auto-generated by YouTube and may contain errors or mistranscriptions. Review unfamiliar words carefully before importing.',
    selectedCount:    (sel: number, tot: number) => `${sel} of ${tot} selected`,
    deselectAll:      'Deselect all',
    selectAll:        'Select all',
    groupRecommended: 'Recommended',
    groupAdvanced:    'Advanced',
    groupLikelyKnown: 'Likely known',
    groupKnown:       'Already known',
    groupFunctionWords: 'Function words',
    groupNames:       'Names',
    groupStudying:    'Already studying',
    studyingInThisDeck: 'Already in this deck',
    studyingSecondCard: 'Becomes a second card with its own progress',
    belowLevelCount:  (n: number) => `${n} word${n !== 1 ? 's' : ''} below your level`,
    rankedForLevel:   'Ranked for your level',
    addToQueue:       'Add to daily queue',
    addToQueueSub:    'Start today — this deck will appear in your daily review',
    creating:         'Creating deck…',
    createAndLearn:   (n: number) => `Create deck (${n} word${n !== 1 ? 's' : ''})`,
    addToDeck:        (n: number) => `Add to deck (${n} word${n !== 1 ? 's' : ''})`,
    back:             'Back',
    reviewWords:      'Review words',
    fromSource:       (src: string) => `From: ${src}`,
    selectAtLeastOne: 'Select at least one word to create a deck.',
    // Shown when no title was typed on the previous screen, so the person
    // knows before they hit Create that a name is coming from somewhere —
    // never the resolved name presented as if it were their own choice.
    autoNamedFrom:    (src: string) => `This deck will be named "${src}" unless you go back and name it yourself.`,
    autoNamedNotice:  'No name was set — this deck will be named automatically from the text.',
  },

  // ── Pro Upgrade page ─────────────────────────────────────────────────────
  proUpgrade: {
    badge:          'CARDSMITH PRO',
    title:          'Level up your learning',
    subtitle:       'Unlock CEFR language courses, grammar reference, and AI-powered features to study smarter.',
    planLabel:      'Pro Plan',
    perMonth:       '/month',
    alreadyPro:     '✓ You\'re on Pro',
    openingPortal:  'Opening…',
    manageSubBtn:   'Manage subscription',
    redirecting:    'Redirecting to checkout…',
    getCta:         'Get CardSmith Pro',
    cancelAnytime:  'Cancel anytime. No hidden fees.',
    faqTitle:       'Questions',
    browseCatalog:  'Browse the catalog first →',

    close:          'Close',
    modalTitle:     'Unlock Pro features',
    modalSubtitle:  'Get the full CardSmith experience with pre-made CEFR courses, grammar reference, AI tutor, and detailed analytics.',
    modalFeatures: [
      { title: 'CEFR Course Catalog', desc: 'A1–C2 pre-made decks for 6 language pairs' },
      { title: 'Grammar Reference',   desc: 'In-depth articles explained in your native language' },
      { title: 'AI Tutor',            desc: '100 AI explanations/day (10× more than free)' },
      { title: 'Mastery Analytics',   desc: 'Per-deck mastery breakdown across all 8 levels' },
    ],
    price:          '$5',
    learnMore:      'Learn more about Pro →',
    redirectingShort: 'Redirecting…',
    features: [
      { title: 'CEFR Course Catalog',  description: '200+ pre-made language courses from A1 to C2, curated by experts.' },
      { title: 'Grammar Reference',    description: 'In-depth grammar articles linked to vocabulary decks for contextual learning.' },
      { title: 'AI Features',          description: 'Generate definitions, examples, and translations powered by AI.' },
    ],
    checklist: [
      'Unlimited access to all CEFR courses (A1–C2)',
      'Grammar articles for every level',
      'AI-generated definitions & examples',
      'Priority support',
      'Early access to new features',
    ],
    faq: [
      { q: 'Can I use CardSmith for free?',           a: 'Yes! The free plan includes unlimited personal decks, Classic & Learning study modes, CSV import/export, and public sharing.' },
      { q: 'What happens to my data if I cancel?',    a: 'Your personal decks and progress are always yours. Pro course content will become inaccessible, but your own decks remain.' },
      { q: 'Is there a lifetime option?',             a: 'Lifetime Pro (plan_expires_at = null) is available for early supporters. Contact us for details.' },
    ],
  },

  // ── Shared / public pages (SharedDeckPage, SharedCoursePage, SharedCardPage) ──
  sharedPages: {
    // Common
    linkExpired:      'This share link may have expired or been removed.',
    logIn:            'Log in',
    signUpFree:       'Sign up free',
    signUpFreeArrow:  'Sign up free →',
    backToExplore:    '← Back to Explore',
    myDecks:          '← My decks',
    viewDeckArrow:    'View deck →',
    // SharedDeckPage
    deckNotFound:     'Deck not found',
    cardCount:        (n: number) => `${n} card${n !== 1 ? 's' : ''}`,
    studyClassic:     '▶ Study Classic',
    trackProgressQ:   'Want to track your progress?',
    trackProgressMsg: 'Sign up free to copy this deck, study with spaced repetition, and track mastery.',
    cardsHeader:      (n: number) => `Cards (${n})`,
    // SharedCoursePage
    courseNotFound:   'Course not found',
    studyCourseQ:     'Want to study this course?',
    studyCourseMsg:   'Sign up free to add this course, study with spaced repetition, and track your mastery.',
    courseContent:    'Course Content',
    standaloneDecks:  'Standalone Decks',
    // SharedCardPage
    cardNotFound:     'Card not found',
    searchDecksPlaceholder: 'Search decks…',
    newDeckOption:    '+ New deck…',
    addToDeck:        'Add to deck…',
    newDeckPlaceholder: 'New deck title',
    addingBtn:        'Adding…',
    addBtn:           'Add to deck',
    loadDecksError:   'Could not load decks. Please close and retry.',
    noDecks:          'No decks found.',
    noDecksYet:       'No decks yet — use "New deck…" above.',
    addError:         'Something went wrong. Please try again.',
    cancelBtn:        'Cancel',
    termLabel:        'Term',
    definitionLabel:  'Definition',
    cardAdded:        (deck: string) => `Card added to ${deck}`,
    cardAddedDesc:    'It will appear in your study queue when it\'s due.',
    saveCardTitle:    'Save this card to your collection',
    saveCardDesc:     'Add it to any of your decks and start learning with spaced repetition.',
    learnWithSrTitle: 'Start learning with spaced repetition',
    learnWithSrDesc:  'Sign up free to create decks, track mastery, and remember more.',
  },

  // ── Course Detail Page ───────────────────────────────────────────────────
  courseDetail: {
    notFound:          'Course not found.',
    reorderError:      'Could not save the new order — your previous order was restored.',
    removeDeckError:   'Could not remove deck from course. Please try again.',
    removeFolderError: 'Could not remove folder from course. Please try again.',
    publicBadge:       'Public',
    learnAll:          '🎯 Learn All',
    settingsBtn:       '⚙ Settings',
    addDeckFolderBtn:  '+ Add Deck / Folder',
    emptyMsg:          'This course has no content yet.',
    addContentBtn:     '+ Add Deck or Folder',
    moveUp:            'Move up',
    moveDown:          'Move down',
    dragToReorder:     'Drag to reorder',
    confirmRemove:     'Remove',
    cancelRemove:      'Cancel',
    removeFolder:      'Remove folder',
    noDecksInFolder:   'No decks in this folder.',
    cardsCount:        (n: number) => `${n} card${n !== 1 ? 's' : ''}`,
    backMyCourses:     '← My Courses',
    backCatalog:       '← Catalog',
  },

  // ── Deck Detail Page ─────────────────────────────────────────────────────
  deckDetail: {
    // Meta badges & count
    systemBadge:         'System',
    publicBadge:         'Public',
    readOnlyBadge:       'Read-only',
    cardCount:           (n: number) => `${n} card${n !== 1 ? 's' : ''}`,
    dueBadge:            (n: number) => `${n} due`,
    learnedPct:          (pct: number) => `${pct}% learned`,
    dueToday:            (n: number) => `${n} due today`,
    studyNowArrow:       'Study now →',
    // Header
    addDescPlaceholder:  'Add a description…',
    moreOptionsLabel:    'More options',
    // Study exercises — two exercises, side by side. Quick prep's label/sub
    // survive unchanged; they moved to the ⋯ menu, not away from this file.
    learnByMemory:       'Study by memory',
    learnByCards:        'Study with flashcards',
    quickPrep:           'Quick prep',
    quickPrepDesc:       'Hardest words · ~10 min',
    inDailyLearning:     'In daily learning',
    inDailyLearningDesc: 'Cards from this deck appear in your smart queue',
    // Card list
    searchCardsPlaceholder: 'Search cards…',
    addCard:             '+ Add card',
    addFirstCard:        '+ Add first card',
    // No leading "+" — unlike `addCard`/`addFirstCard` above, which are
    // icon-less buttons whose literal plus is the only one they get, this
    // string is rendered beside a <Plus> icon (InsertDivider) and carried a
    // second one.
    addCardHere:         'Add card here',
    noCardsTitle:        'No cards yet',
    noCardsDesc:         'Add cards to start learning this deck',
    noCardsMatch:        (q: string) => `No cards match "${q}"`,
    deselectAll:         'Deselect all',
    selectAll:           (n: number) => `Select all (${n})`,
    // Before the deck-wide count has arrived. Never a loaded-set number in its
    // place: a wrong count beside Delete is the defect §23 item 9 describes.
    selectAllPending:    'Select all',
    nSelected:           (n: number) => `${n} selected`,
    nSelectedPending:    'Counting…',
    nCardsSelected:      (n: number) => `${n} card${n !== 1 ? 's' : ''} selected`,
    nCardsSelectedPending: 'Counting…',
    // Select-all covers the whole deck, and a search narrows to matches the
    // server has not been asked to select over. Refusing and saying so beats
    // selecting the matches that happen to be on screen.
    selectAllUnavailableSearch: 'Clear the search to select the whole deck.',
    countUnavailable:    'Could not read this deck’s size — try again before a bulk action.',
    // A whole-deck selection has no id list, and Copy on the desktop bar is the
    // one action still phrased that way.
    bulkCountUnknown:    'Waiting for this deck’s size…',
    deleteBulkTitlePending: 'Delete the selected cards?',
    toastDeletePartial:  (deleted: number, requested: number) =>
      `Deleted ${deleted} of ${requested} cards.`,
    toastMovePartial:    (moved: number, requested: number) =>
      `Moved ${moved} of ${requested} cards.`,
    // Overflow menu
    menuStudyGroup:      'Study',
    menuDeckGroup:       'Deck',
    menuEditDetails:     'Edit details',
    menuEditDetailsSub:  'Name, description',
    // Desktop deck bar (ADR-067, thirteenth amendment)
    barCopyToLibrary:    'Copy to library',
    detailsDialogTitle:  'Deck details',
    detailsNameLabel:    'Name',
    detailsDescLabel:    'Description',
    detailsSave:         'Save',
    deckLanguageLabel:   'Deck language',
    deckLanguageNote:    'Existing cards keep their text; fill them again to get the new language.',
    deckLanguageError:   'The language could not be changed.',
    aboutDeckEyebrow:    'About this deck',
    menuEditCards:       'Edit cards',
    menuEditCardsSub:    'Tap any card to edit',
    menuContentGroup:    'Content',
    menuAddFromText:     'Add words from text',
    menuImportCsv:       'Import CSV',
    menuExporting:       'Exporting…',
    menuExportCsv:       'Export CSV',
    menuSharingGroup:    'Sharing',
    menuMakePrivate:     'Make private',
    menuMakePrivateSub:  'Remove from public search',
    menuMakePublic:      'Make public',
    menuMakePublicSub:   'Anyone can find and view',
    menuCopyShareLink:   'Copy share link',
    menuCopying:         'Copying…',
    menuCopyToLibrary:   'Copy to my library',
    menuCopyToLibrarySub: 'Creates an editable personal copy',
    menuDeleteDeck:      'Delete deck…',
    // Confirm modals
    deleteDeckTitle:     'Delete Deck?',
    deleteDeckMsg:       (title: string, count: number) => `"${title}" and all ${count} cards will be permanently deleted.`,
    deleteBulkTitle:     (n: number) => `Delete ${n} card${n !== 1 ? 's' : ''}?`,
    // Was "This cannot be undone." — no longer true, and a warning that
    // overstates the stakes is as misleading as one that understates them.
    deleteBulkMsg:       'You can undo this for a short while afterwards.',
    deletingLabel:       'Deleting…',
    deleteLabel:         'Delete',
    cancelLabel:         'Cancel',
    // Top strip's search toggle (brief §15, 2026-08-04 rework) — the one
    // always-visible entry point, in view mode, editing mode, and system decks
    // alike.
    deckSearchToggleAria: 'Search this deck',
    // Proofread + bulk bar
    tapToEdit:           'Tap any card to edit',
    doneBtn:             'Done',
    moveToBtn:           'Move to',
    copyToBtn:           'Copy to',
    deleteBtn:           'Delete',
    cancelSelectionLabel: 'Cancel selection',
    // Undo toast
    undoFailed:          'Undo failed — please retry.',
    dismissBtn:          'Dismiss',
    retryBtn:            'Try again',
    movedCards:          (n: number, deck: string) => `Moved ${n} card${n !== 1 ? 's' : ''} to "${deck}"`,
    copiedCards:         (n: number, deck: string) => `Copied ${n} card${n !== 1 ? 's' : ''} to "${deck}"`,
    // The delete offer states what Undo restores, not merely that it exists —
    // the cards are off the screen, so the reader has nothing to look at while
    // deciding whether it is worth tapping.
    deletedCardsUndoable: (n: number) =>
      `Deleted ${n} card${n !== 1 ? 's' : ''}. Undo restores them with their content and study progress.`,
    deletedPartialUndoable: (deleted: number, requested: number) =>
      `Deleted ${deleted} of ${requested} cards. Undo restores those ${deleted} with their content and study progress.`,
    undoBtn:             'Undo',
    undoDeleteAria:      'Undo the deletion and restore the cards',
    undoTransferAria:    'Undo the last card transfer',
    // Toast messages
    // The cards ARE back — only the source deck's order-restore sub-step
    // failed, which is why this doesn't say "Undo failed": nothing here
    // invites a retry, because retrying the move itself would be wrong (it
    // already happened). Plain past tense, no hedge — "may have shifted"
    // reported an outcome that was in fact certain, not probabilistic.
    toastOrderNotRestored: 'Cards returned, but their original order could not be restored.',
    toastUndone:         'Undone.',
    // A copy's Undo removes only the cards that copy created, and only while
    // they are still in the deck it copied them into. One the reader has since
    // moved or deleted is left alone — said plainly, because "Undone" over a
    // partial would be claiming something that was not checked.
    toastCopyUndoPartial: (removed: number, expected: number) =>
      `Removed ${removed} of ${expected} copied cards — the rest are no longer in that deck.`,
    // Four outcomes, and the three that are not a clean success say which part
    // did not come back. A restore that returned the cards but dropped their
    // scheduling would otherwise report "Restored" and leave the reader with no
    // way to notice their progress had been reset.
    toastRestored:       (n: number) => `${n} card${n !== 1 ? 's' : ''} restored.`,
    toastRestorePartial: (back: number, expected: number) =>
      `Restored ${back} of ${expected} cards — the rest could not be brought back.`,
    toastRestoredWithoutProgress: (n: number) =>
      `${n} card${n !== 1 ? 's' : ''} restored, but their study progress could not be — they start again as new.`,
    toastRestoredWithoutLinks: (n: number) =>
      `${n} card${n !== 1 ? 's' : ''} restored. Their dictionary links could not be reconnected.`,
    toastDeleted:        (n: number) => `Deleted ${n} card${n !== 1 ? 's' : ''}.`,
    toastLinkCopied:     'Link copied!',
    toastReorderFailed:  'Reorder failed — changes reverted.',
    toastCopiedToLibrary: 'Copied to your library.',
    toastEditDetailsError: 'Could not save changes.',
    // Card row per-card overflow
    dragToReorder:       'Drag to reorder',
    // The desktop list row's selection box, which is present at rest rather
    // than appearing once a selection exists (brief §23).
    selectCardAria:      'Select this card',
    cardOptionsLabel:    'Card options',
    editCard:            'Edit card',
    moveToDeck:          'Move to deck',
    lookUpReference:     'Look up reference',
    closeReference:      'Close reference panel',
    // Desktop two-panel editor (brief §22)
    loadingMoreCards:    'Loading more cards…',
    // Read by screen readers in place of the dash. The dash itself is the
    // visual: this says what an em dash on a row means.
    desktopFieldMissing: 'Not filled in',
    // Absence is WRITTEN into the slot the field would have occupied
    // (DESIGN_SYSTEM.md §12) — words cannot be mistaken for a loading state,
    // blank rectangles can. Lower case, no dash, no frame: this is the
    // absence ROLE, not a field label returning by the back door (§13).
    rowNoDefinition: 'no definition',
    rowNoExample: 'no example',
    rowNoTranslation: 'no translation',
    // A repeat is a labelled type tag with its count — never a bare numeral
    // (§12.6: nothing on the row face is an unlabelled mark).
    rowRepeatDefinition: 'DEF',
    rowRepeatExample: 'EX',
    rowRepeatTranslation: 'TR',
    rowRepeatCount: (n: number): string => `×${n}`,
    rowAttachmentCount: (kind: string, n: number): string =>
      n > 1 ? `${kind} ×${n}` : kind,
    // The mastery column names the unstudied state rather than leaving an
    // empty scale to be read as a loading bar (§12.7).
    rowMasteryNew: 'NEW',
    // A click opens a card to be READ (ADR-067 amendment) — so this no longer
    // says "pick a card to edit it". The shortcut list below it is the
    // editing cycle, named as such.
    desktopPanelEmptyTitle: 'Pick a card to read it',
    desktopPanelEmptyBody: 'Click a card on the left, or use one of these:',
    // The selected card is still selected and is not among the rows currently
    // listed. Said here because the pane is the only place that can say it —
    // the row that would have carried the mark is precisely what is missing.
    // The card is named so the reader knows their place was kept.
    selectionHiddenTitle: (term: string) => `“${term}” is still open, and not in this list`,
    selectionHiddenBySearch: (query: string) => `It does not match “${query}”.`,
    selectionHiddenByFilter: 'The filter is hiding it.',
    kbdClearSearch:      'Clear the search and go back to it',
    selectionHiddenFilterWayBack: 'Choose "All" in the filter to go back to it.',
    kbdOpenFirstCard:    'Open the first card',
    kbdNewCard:          'New card below',
    kbdNextField:        'Next field / previous field — past the last one, the next card',
    kbdAddField:         'Add a field',
    kbdDeleteField:      'Delete the focused field',
    kbdSearchDeck:       'Search this deck',
    kbdSearchEverywhere: 'Search everywhere',
    kbdFinish:           'Finish and leave the card',
    // Narrow-width sheet (brief §23 item 8): below the two-pane threshold the
    // selected card opens as a sheet over the full-width list.
    sheetBackToList:     '‹ All cards',
    sheetCloseAria:      'Close and return to the list',
    // Desktop reading vs editing (ADR-067 amendment 2026-09-08). A click opens
    // a card to be READ; editing is entered explicitly, per card or per deck.
    readCardEdit:        'Edit',
    readCardEditAria:    'Edit this card',
    readCardEditHint:    (combo: string) => `${combo} to edit`,
    editCardDone:        'Done',
    editCardDoneAria:    'Finish editing this card',
    menuEditAllCards:    'Edit every card',
    menuEditAllCardsAria: 'Edit every card in this deck',
    deckEditModeOn:      'Editing every card',
    deckEditModeOff:     'Done',
    deckEditModeOffAria: 'Stop editing every card in this deck',
    deckEditEmptyTitle:  'Editing every card',
    deckEditEmptyBody:   'Pick a card on the left and it opens ready to edit. This panel keeps its width meanwhile, so the cards do not shift as you move between them.',
    // Pane-level paste recognition (brief §23 item 8): pasting two or more
    // lines anywhere on the list is offered as a bulk add, never applied
    // silently.
    pasteOfferTitle:     (n: number) => `That looks like ${n} word${n === 1 ? '' : 's'}, not one card.`,
    pasteOfferBody:      'CardSmith will pull these into new cards at the end of this deck through the same text-import flow as "Import text…" — review the list before anything is saved.',
    pasteOfferMore:      (n: number) => `+${n} more`,
    pasteOfferAdd:       (n: number) => `Add ${n} card${n === 1 ? '' : 's'}…`,
    pasteOfferDiscard:   'Discard paste',
    deleteCardTitle:     'Delete this card?',
    deleteCardMsg:       'The card and everything on it goes, along with its study progress.',
    // Inline edit form
    termLabel:           'Term',
    definitionLabel:     'Definition',
    removeSide:          'Remove side',
    addSide:             '+ Add side',
    savingLabel:         'Saving…',
    saveBtn:             'Save',
    // Audio player
    audioUnavailable:    'Audio unavailable',
    audioProgress:       'Audio progress',
    pauseLabel:          'Pause',
    playLabel:           'Play',
    // Deck picker modal
    moveTo:              (n: number) => `Move ${n} card${n !== 1 ? 's' : ''} to…`,
    copyTo:              (n: number) => `Copy ${n} card${n !== 1 ? 's' : ''} to…`,
    moveConsequence:     'They leave this deck.',
    copyConsequence:     'They stay in this deck as well.',
    searchDecksPlaceholder: 'Search decks…',
    newDeckOption:       '+ New deck…',
    newDeckPlaceholder:  'New deck title',
    loadDecksError:      'Could not load decks. Please close and retry.',
    noDecksFound:        'No decks found.',
    noOtherDecks:        'No other decks yet — use "New deck…" above.',
    transferError:       'Something went wrong. Please try again.',
    moving:              'Moving…',
    copying:             'Copying…',
    moveBtn:             'Move',
    copyBtn:             'Copy',
    // Fill's own budget report. The rest of this block went with the inline
    // bulk-add editor (ADR-067 Decision F) — per-row autofill was that
    // surface's, and Fill is the deck-wide one that replaced it.
    autofillErrorRateLimit:   'Daily AI limit reached.',
    // Back navigation
    backCatalog:         '← Catalog',
    backMyDecks:         '← My Decks',
    backMyCourses:       '← My Courses',
    // Same three destinations, without the baked-in arrow glyph — the desktop
    // screen-header bar renders its own chevron icon next to this text, so a
    // second text arrow would double up (ADR: screen-header bar).
    parentCatalog:       'Catalog',
    parentMyDecks:       'My Decks',
    parentMyCourses:     'My Courses',
    // Mobile card feed (deck-feed-v2 shell)
    // The empty deck's two equal entrances (brief §22). "Add cards one by one"
    // was retired with the separate bulk-create screen it opened: the create
    // entrance is now the same in-place flow `+ Card` uses.
    emptyCreateFirstCard: 'Create the first card',
    emptyImportText:      'Import text',
    savedLabel:          'Saved',
    jumpToLatestBtn:     'Jump to latest',
    // Pinned viewing-mode bar (ADR-067 Decision 1, brief §9)
    barStudy:            'Study',
    barAddCard:          'Card',
    barAddCardAria:      'Add a card',
    barEdit:             'Edit',
    // Pinned editing-mode bar (ADR-067 Decision 2, mode entry/exit)
    modeWordEditing:     'Editing',
    barExitEditingAria:  'Done editing',
    // Pinned editing-mode bar — + Field (brief §11)
    barAddField:         'Field',
    barAddFieldAria:     'Add a field',
    // Previous/Next + the counter's three grammars (brief §13)
    navPrevAria:         'Previous card',
    navNextAria:         'Next card',
    navCounter:          (n: number, total: number) => `${n} OF ${total}`,
    navCounterDash:      (total: number) => `— OF ${total}`,
    navShown:            'SHOWN',
    navFound:            'FOUND',
    navJumpAria:         'Jump to a card',
    navClearSearchAria:  'Clear search',
    navJumpTitle:        'Go to card',
    navJumpPlaceholder:  'Number',
    navJumpGo:           'Go',
    navJumpCloseAria:    'Close quick jump',
    navJumpLoading:      'Loading cards…',
    // The add-affordance shown when the card a walk lands on has no field of
    // the type being edited (brief §13).
    navAddMissingField:  (type: string) => `Add ${type}`,
  },

  // Shared between the deck-feed card (DeckCard) and the full-screen editor:
  // Fill action + meaning arrows (Prompt 6), deck-feed card states (Prompt 9)
  // Manual reset (ADR-098): the learner says they have forgotten. Both actions
  // are irreversible — the copy says so, and never offers an undo.
  masteryReset: {
    cardAction:          'I’ve forgotten this word',
    cardActionAria:      'Reset this word’s progress',
    cardTitle:           (term: string) => `Reset progress on “${term}”?`,
    cardMsg:             'It goes back to the start of learning and comes up at the beginning of your next session. Its current progress can’t be restored.',
    deckMenuItem:        'Reset mastered words…',
    deckMenuItemSub:     'For words you’ve forgotten',
    deckTitlePending:    'Reset mastered words?',
    deckTitle:           (n: number) => `Reset ${n} mastered ${n === 1 ? 'word' : 'words'}?`,
    deckMsg:             'They go back to the start of learning and return a few at a time, at the beginning of your next sessions. Words you’re still learning aren’t affected. Their progress can’t be restored.',
    deckNoneTitle:       'No mastered words here',
    deckNoneMsg:         'Only mastered words are reset, and this deck has none yet.',
    countChanged:        'The number of mastered words has changed. Check it and confirm again.',
    failed:              'Couldn’t reset. Try again.',
    confirm:             'Reset progress',
    confirming:          'Resetting…',
    cancel:              'Cancel',
    close:               'Close',
    deckDone:            (n: number) => `${n} ${n === 1 ? 'word' : 'words'} reset`,
  },
  cardBubble: {
    fillBtn:            'Fill',
    fillLoading:        'Filling…',
    fillErrorGeneric:   'Could not fill this card. Please try again.',
    // ── The meaning chooser (DESIGN_SYSTEM.md §14) ──
    meaningLabel:       'Meaning',
    meaningNotLinked:   'none linked yet',
    meaningNoDictionaryLink: 'No dictionary link',
    meaningNoDefinition: 'No definition in the dictionary',
    meaningCapsuleAria: (gloss: string | null, locked: boolean): string =>
      `Meaning: ${gloss ?? 'none linked yet'}${locked ? ' (kept by your definition)' : ''}. Show all meanings`,
    // The term and the count as prose, never as a position (§14 item 3).
    meaningListHeading: (term: string) => `Meanings of “${term}”`,
    meaningListCount:   (n: number) => `${n} ${n === 1 ? 'sense' : 'senses'} in the dictionary`,
    meaningListInstruction: 'choose one to link this card',
    meaningLinked:      'Linked',
    // What linking does, stated before it is done. The translation clause
    // follows the card: a translation already on the card is kept by the link
    // (ADR-063 Amendment 1 — the card's own side wins), so promising to
    // re-resolve it would be false.
    meaningFooter: (translationOnCard: boolean): string => translationOnCard
      ? 'Linking replaces the definition and re-resolves the grammatical form. The translation already on the card, and anything in the example and notes, are kept.'
      : 'Linking replaces the definition and re-resolves the translation and grammatical form. Anything in the example and notes is kept.',
    // Said in terms of the reader's own text, not of the linkage underneath
    // it: what would go wrong, then what releases the hold (§14 item 5).
    meaningLocked: 'You wrote this definition, so the meaning stays with it — linking another would give the card a translation and grammatical form your text contradicts. Clear the definition to choose a different one.',
    // The translation label's provenance (§14 item 7).
    translationFromMeaning: 'from this meaning',
    translationTyped:   'typed',
    // One next step at a time (see notInStudyKind in utils/cardSideDisplay.ts
    // for the selection rule) — never both fields named in one line, and the
    // barrier itself lives in notInStudyExplanation, not here.
    //
    // Return type annotated explicitly as `string`: this locale object has no
    // type of its own (`AppTranslations` is `typeof en`), so without this a
    // function's return type is inferred as the literal union of exactly
    // these two English phrases — which no other locale's translation could
    // ever satisfy, breaking `ru: typeof en` on the words themselves rather
    // than on any real shape mismatch. `string` is what this field always
    // meant; every other locale's function already returns it structurally,
    // just with different words, so this widens nothing that was load-bearing.
    notInStudyLabel:    (missing: 'translation' | 'definition'): string =>
      missing === 'translation' ? 'Add a translation' : 'Add a definition',
    notInStudyInfoAria: 'Why this matters',
    notInStudyExplanation: 'Both a translation and a definition are needed to build these exercises. Without them, classic study still accepts the card as it is.',
    notInStudyExplanationDismiss: 'Got it',
    // A write failed and the change is standing unsaved (ADR-072 Decision 4,
    // amended by ADR-073 for this wording, further amended by ADR-077). The
    // label reports what happened — a save that failed — rather than
    // implying one is still pending or coming: nothing here retries on its
    // own, so the copy must not read as a promise, and must not mention
    // reloading, which plays no part in clearing the mark. The explanation
    // says the part the person cannot see for themselves — that their text
    // stayed in the field rather than reaching the server — and stops there:
    // ADR-073's own "it will be sent the next time this card is saved"
    // clause read as an assurance that the system would eventually deliver
    // the edit, which is false the moment the app closes before another save
    // (ADR-077).
    unsavedLabel:       "Couldn't save",
    unsavedExplanation: 'This edit stayed in the field but did not reach the server.',
    studiedBadge:       'Studied',
    notStudiedLabel:    'Not studied',
    ordinalPosition:    (n: number) => {
      const suffix = (n % 10 === 1 && n % 100 !== 11) ? 'ST'
        : (n % 10 === 2 && n % 100 !== 12) ? 'ND'
        : (n % 10 === 3 && n % 100 !== 13) ? 'RD' : 'TH'
      return `${n}${suffix}`
    },
    repeatCount:        (kindLabel: string, count: number) => `${count} ${kindLabel}s`,
    collapseCardAria:   'Collapse card',
    expandCardAria:     'Expand card',
    audioFieldCaption:  "A field, not the term's pronunciation",
    // Opens the file/URL widget under an image or audio field's chip. The chip
    // is the resting state (§5.3 — an attachment costs one row like any other
    // field); this is how the file itself is changed without leaving the card.
    replaceAttachmentBtn:       'Replace',
    replaceAttachmentCancelBtn: 'Cancel',
    replaceAttachmentAria:      (label: string) => `Replace ${label}`,
    // An image pasted straight onto the card — a file from the clipboard, or a
    // URL that names one. The field appears at the end of the card's stack
    // like any other, so the report is what says WHERE it went; without it a
    // paste on a card scrolled past its own end reads as nothing happening.
    imagePastedToast:           'Image added from clipboard',
    imagePasteFailedToast:      'Could not read that image',
    // Active (editing) card — brief §5.3 / §12
    cardNumber:         (n: number) => `Card ${n}`,
    fieldDeletedMsg:    (label: string) => `${label} deleted`,
    // ADR-066 Decision 1 — a reorder moved a different field of a repeated kind
    // to the front, so Learn asks from that one now. Said at the moment of the
    // move, because the alternative is finding out mid-study.
    fieldPromptChangedMsg: (labels: string) => `Order changed — Learn now asks from the first ${labels}`,
    undoBtn:            'Undo',
  },

  // Mobile full-screen card editor (Prompt 7)
  cardEditor: {
    saveErrorGeneric:       'Could not save. Please try again.',
    termLabel:              'Term',
    translationLabel:       'Translation',
    definitionLabel:        'Definition',
    exampleLabel:           'Example',
    noteLabel:              'Note',
    imageLabel:             'Image',
    audioLabel:             'Audio',
    linkLabel:              'Link',
    dialogLabel:            'Dialog',
    customFieldLabel:       'Custom field',
    removeFieldAria:        (label: string) => `Remove ${label}`,
    // Names the whole gesture, drag and keys alike — the handle is one control
    // with two ways of being worked (brief §14).
    reorderFieldAria:       (label: string) => `Reorder ${label} — drag, or use the arrow keys`,
    termPlaceholder:        'Type the word…',
    translationPlaceholder: 'Enter the translation…',
    definitionPlaceholder:  'Enter the definition…',
    examplePlaceholder:     'Enter an example…',
    notePlaceholder:        'Enter a note…',
    linkPlaceholder:        'https://…',
    dialogPlaceholder:      'Enter a dialogue…',
    customFieldNamePlaceholder: 'Field name',
    // A custom field's name is the half its author writes, so it stays editable
    // after creation — the chooser asks for it once and would otherwise make a
    // typo permanent.
    renameFieldAria:        (label: string) => `Rename ${label}`,
    deleteCardTitle:        'Delete card?',
    deleteCardMsg:          'This cannot be undone.',
    deletingLabel:          'Deleting…',
    deleteBtn:              'Delete',
    cancelLabel:            'Cancel',
  },

  // Compact inline type chooser for + Field (brief §11) — not a modal, not a
  // sheet: a small popover anchored above the editing bar. Type labels are
  // borrowed from cardEditor (translationLabel/definitionLabel/exampleLabel/
  // customFieldLabel) rather than duplicated here.
  fieldChooser: {
    title:            'Add field',
    closeAria:        'Close',
    addBtn:           'Add',
    // One parameterised aria for every fixed type — nine hand-written strings
    // would be nine places for a type's name to drift from the pill beside it.
    addFieldAria:     (typeName: string) => `Add a ${typeName} field`,
    customAria:       'Add a custom field',
  },

  // The mobile Reorder screen (brief §4.4).
  deckReorder: {
    title:        'Reorder cards',
    subtitle:     'Drag a handle to move a card',
    done:         'Done',
    loading:      'Loading cards…',
    loadingMore:  'Loading more…',
    moreBelow:    (n: number) => `Scroll to load ${n} more`,
    // The landing zone names the number the card will carry, not the one it
    // has — the reader is placing it, and the outcome is what they are choosing.
    dropsHere:    (n: number) => `Drops here · becomes ${n}`,
    handleAria:   (term: string) => `Move ${term} — drag, or use the arrow keys`,
    // A drop that did not save. The list still shows the arrangement the reader
    // made, so it has to say plainly that the deck no longer matches it.
    saveFailed:   "Couldn't save that move — the deck's order is not what you see here. Leave and come back to reload it.",
    // Why the entry is refused while a filter is on (brief §4.4): dragging
    // within a subset does not say where the card goes among the cards hidden
    // from it.
    menuReorderCards:     'Reorder cards',
    menuReorderSub:       'Drag cards into a new order',
    menuReorderFiltered:  'Not while a filter is on — show all cards first',
  },

  // Mobile deck-management chrome — search/filters, Fill-all, multi-select
  // (Prompt 8). Latent by design: none of this renders until summoned.
  deckManagement: {
    menuManageGroup:      'Manage',
    // Renamed from "Search cards" — search moved to the top strip's own
    // always-visible control (brief §15, 2026-08-04 rework) and is no longer
    // reached only through this menu. What is left here is the chip filter,
    // which stays its own separate affordance rather than sharing search's
    // compact panel.
    menuFilterCards:      'Filter cards',
    menuFillEmptyCards:   'Fill empty cards',
    closeSearchAria:      'Close search',
    closeFilterAria:      'Close filter',
    chipAll:              'All',
    chipNoTranslation:    'No translation',
    chipNoExample:        'No example',
    chipNotFilled:        'Not filled',
    resultCount:          (n: number, total: number) => `${n} of ${total} cards`,
    clearFilters:         'Clear',
    noCardsMatchFilter:   'No cards match.',
    // In-deck search (§15). The wording carries the boundary to global search:
    // this box finds cards in THIS deck and does nothing else with them.
    searchInDeckPlaceholder: 'Find in this deck…',
    searchRunning:        'Searching this deck…',
    searchResultCount:    (n: number) => `${n} card${n !== 1 ? 's' : ''} in this deck`,
    searchFailed:         'Search could not run.',
    searchRetry:          'Try again',
    searchNotInThisDeck:  'Not in this deck.',
    searchEverywhere:     'Search everywhere',
    searchAddToDeck:      'Add it to this deck',
    chipDroppedForSearch: 'Filter turned off so the search can reach the whole deck.',
    undo:                 'Undo',
    // Quick jump landed past the pages loaded so far, so the feed now starts
    // at the card's own page rather than at the top of the deck.
    anchoredFrom:         (n: number) => `Showing from card ${n}`,
    anchoredBackToStart:  'Back to the start',
    // A `?card=` link arrived at a card a chip or a search was hiding, so the
    // narrowing was turned off to show it — said plainly, in the same register
    // as `chipDroppedForSearch` above, which is the precedent for announcing a
    // filter this screen switched off on the reader's behalf.
    deepLinkNarrowingCleared: 'Filter turned off to show this card.',
    // The other outcome, and it has to be stated: an arrival that finds nothing
    // leaves a page with no mark on it, which reads as a link that did nothing.
    deepLinkNotFound:     'That card could not be found in this deck.',
    fillAllPreflightLoading: 'Checking what needs filling…',
    fillAllTitle:         (n: number) => `Fill ${n} empty card${n !== 1 ? 's' : ''}`,
    fillAllSubtitle:      'Only empty fields get filled. Anything you typed stays as it is.',
    fillAllDictionaryRow: (n: number) => `${n} from dictionary`,
    fillAllDictionaryNote: 'Instant · free',
    fillAllAiRow:         (n: number) => `${n} via AI`,
    fillAllAiNote:        'No dictionary entry',
    fillAllConfirmBtn:    (n: number) => `Fill ${n} Card${n !== 1 ? 's' : ''}`,
    fillAllNothingToFill: 'This deck has nothing left to fill.',
    fillAllBudgetWarning: (remaining: number) => `Only ${remaining} AI fill${remaining !== 1 ? 's' : ''} left today — the rest will stop when the budget runs out.`,
    // The cost half of the disclosure. The sheet already names what the action
    // will spend; without what there is to spend, the reader cannot tell
    // whether it fits. Shown whether or not the budget is short.
    fillAllAllowance:     (remaining: number, limit: number) => `${remaining} of ${limit} AI fills left today`,
    // Said out loud rather than shown as a full allowance. An unreadable
    // counter is not the same as no limit, and printing the daily maximum
    // would promise budget nobody checked.
    fillAllAllowanceUnknown: 'How much of your daily AI allowance is left could not be checked right now.',
    fillAllErrorGeneric:  'Could not start Fill-all. Please try again.',
    fillAllProgress:      (done: number, total: number) => `Filling cards… ${done}/${total}`,
    fillAllStopBtn:       'Stop',
    fillAllStopping:      'Stopping…',
    fillAllDictionaryThenAi: (n: number) => `${n} from dictionary · AI batch starts after`,
    fillAllDone:          'Done',
    fillAllStopped:       'Stopped — filled cards were kept',
    fillAllFailed:        'Fill-all failed',
    exitSelectAria:       'Exit selection',
    fillSelectedBtn:      'Fill',
    moveSelectedBtn:      'Move',
    copySelectedBtn:      'Copy',
    deleteSelectedBtn:    'Delete',
    fillingSelected:      (done: number, total: number) => `Filling ${done} of ${total}…`,
    // Fill resolves the selection on the server before it starts, so the total
    // it quotes is the deck's rather than the loaded pages'. This is the gap
    // between tapping Fill and knowing how much work there is.
    fillResolving:        'Working out how many cards…',
    fillCouldNotResolve:  'Could not read the selection — nothing was filled.',
    // The multiselect bar's Fill preview — same dictionary/AI split sheet as
    // Fill-all (fillAllTitle/fillAllConfirmBtn etc. are reused verbatim,
    // since "Fill N empty cards" reads correctly whether N is the deck or a
    // selection), plus these two selection-specific strings.
    fillSelectionNothingToFill: 'Nothing in this selection needs filling.',
    fillSelectionErrorGeneric: 'Could not work out what this would cost. Please try again.',
  },

  // Not Found page
  notFound: {
    title:   'Page not found',
    message: "The page you're looking for doesn't exist or may have been moved.",
    goHome:  'Go home',
  },

  // Billing Success page
  billing: {
    successTitle:   'Welcome to Pro!',
    successDesc:    'Your subscription is active. All Pro features — CEFR courses, grammar articles, and AI tools — are unlocked.',
    redirectingMsg: 'Redirecting you home in a moment…',
    goToHome:       'Go to home',
  },

  // Grammar pages (stubs)
  grammarList: {
    title:    'Grammar',
    subtitle: 'Grammar reference articles — Pro feature.',
  },
  grammarArticle: {
    title: 'Grammar Article',
  },

  // Notification push prompt
  notification: {
    ariaLabel:  'Enable study reminders',
    title:      'Stay on track',
    desc:       "We'll remind you to practice so you don't lose your streak. No spam — only daily study nudges.",
    enabling:   'Enabling…',
    enableBtn:  'Enable reminders',
    notNow:     'Not now',
  },

  // Public password-reset page (ADR-061 §4) — reached from a reset link, no session
  resetPassword: {
    title:        'Set a new password',
    desc:         'Choose a new password for your account. For your security, all devices will be signed out.',
    newPassword:  'New password',
    confirm:      'Confirm new password',
    submit:       'Set new password',
    saving:       'Saving…',
    successTitle: 'Password updated',
    successBody:  'Your password has been changed. Sign in with your new password to continue.',
    signIn:       'Go to sign in',
    errTooShort:  'New password must be at least 8 characters.',
    errMismatch:  'New passwords do not match.',
    // Shown for expired / used / unknown / malformed tokens — one message, on purpose.
    errInvalid:   'This reset link is no longer valid. It may have expired or already been used — ask for a new one.',
    errMissing:   'This reset link is incomplete. Open the full link from your reset message, or ask for a new one.',
    errGeneric:   'Something went wrong. Please try again in a moment.',
  },

  // Word reference panel (WordReferencePanel + WordReferenceTrigger) — tabbed
  // lookup opened from Learn and DeckDetail. Added after the i18n coverage
  // pass; not previously catalogued.
  wordReference: {
    triggerAriaLabel:  'Open word reference',
    triggerTitle:      'Word reference',
    tabExamples:       'Examples',
    tabReference:      'Reference',
    tabAi:             'AI',
    youglishAriaLabel: (term: string) => `Hear "${term}" on YouGlish`,
    youglishTitle:     'Listen on YouGlish', // "YouGlish" itself: i18n-exempt: brand name
    closeAria:         'Close reference panel',
    panelAria:         'Word reference',
    tryAgain:          'Try again',
    noExamplesFound:   (term: string) => `No example sentences found for "${term}".`,
    examplesLoadError: 'Could not load example sentences.',
    aiLoadError:       'Could not load AI explanation.',
    referenceLoadError: 'Could not load reference data.',
    noReferenceData:   'No reference data available for this word.',
    irregularForms:    'Irregular forms',
    synonyms:          'Synonyms',
    antonyms:          'Antonyms',
  },

  // Deck status badge (DeckStatusBadge) — "marks" shown on deck cards.
  deckStatus: {
    paused:   'Not in daily',
    readonly: 'Read-only',
    public:   'Public',
    system:   'System',
    practice: 'Practice · levels unchanged',
  },

  // Card editor Audio/Image side inputs.
  sideInput: {
    reRecord:     'Re-record',
    record:       'Record',
    replaceFile:  'Replace file',
    uploadFile:   'Upload file',
    cancel:       'Cancel',
    close:        'Close',
    replace:      'Replace',
    editUrl:      'Edit URL',
    fromUrl:      'From URL',
    save:         'Save',
    add:          'Add',
    remove:       'Remove',
    stop:         'Stop',
    useThis:      'Use this',
    // Audio
    errChooseAudioFile:  'Please choose an audio file (MP3, M4A, WAV, OGG, etc.)',
    errAudioTooLarge:    (mb: number) => `Audio file too large — max ${mb} MB.`,
    errCouldNotReadAudio: 'Could not read audio file.',
    errUrlNotAudio:      'URL must point to an audio file (.mp3, .wav, .m4a, etc.)',
    supportedAudioFormats: (list: string) => `Supported: ${list}`,
    invalidAudio:        'Invalid audio — re-record or upload a file to replace this side.',
    clickToUploadAudio:  'Click to upload audio',
    waitingForMic:       'Waiting for microphone access…',
    previewRecorded:     (time: string) => `Preview — ${time} recorded`,
    recordingFailed:     'Recording failed.',
    // Image
    errImageTooLarge:    (mb: number) => `Image too large — max ${mb} MB. Choose a smaller file.`,
    errCouldNotProcessImage: 'Could not process image. Try a different file.',
    couldNotLoadImage:   'Could not load image',
    clickToChooseImage:  'Click to choose image',
  },

  // Mastery scale accessible name (MasteryBadge 'dot' mode, MasteryMeter).
  // The scale is a number out of a fixed max, not a word — no per-level label
  // exists to display or to pronounce; `ariaLabel` states the same numeral a
  // sighted reader sees (the fraction), so a screen reader gets a name for
  // the scale without reintroducing a claim about memory the level doesn't
  // make on its own.
  mastery: {
    ariaLabel: (level: number, max: number): string => `Mastery: ${level} of ${max}`,
    // Deck row (desktop left panel) never-studied caption — a different state
    // entirely from a real level (no MasteryState row exists yet), so it is
    // not derived from the scale at all. The deck row's in-progress caption
    // is the bare fraction ("N/8") — locale-neutral, so it needs no entry here.
    neverStudied: 'Not studied yet',
  },
}
